import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '../../../hooks/AuthContext';
import { useTask } from '../contexts/TaskContext';
// SUPABASE SWITCH: revert to '../services/firestore' for Firebase
import { subscribeToMyTasks, migrateAllAsignados, migrateWorkspaceId, updateTask, updateSubtask, deleteTask } from '../services/firestore';
import { FiCalendar, FiUser, FiCheckCircle, FiCircle, FiClock, FiList, FiGrid, FiChevronRight, FiRepeat, FiFolder, FiMapPin, FiFilter, FiSearch, FiEdit3, FiTrash2, FiPlus, FiFlag, FiUserPlus, FiX, FiBold, FiItalic, FiUnderline, FiLink, FiImage, FiMinus, FiZap, FiMessageCircle, FiSend, FiCopy, FiChevronDown, FiFolderPlus, FiMaximize2, FiMinimize2, FiCheck, FiGlobe, FiStar, FiBook } from 'react-icons/fi';
import EbookPreview from '../components/EbookPreview';
import { CalendarDays, Repeat, ChevronDown, X, Plus } from 'lucide-react';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, orderBy, onSnapshot, serverTimestamp, getDocs, where } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { format, isToday, isTomorrow, isPast } from 'date-fns';
import { es } from 'date-fns/locale';
import toast from 'react-hot-toast';
import MyTasksBoard from '../components/MyTasksBoard';
import TaskCreateModal from '../components/TaskCreateModal';
import { defaultsForColumn } from '../services/myTasksBoard';

// ── Helpers ──
const parseDate = (dateValue) => {
  if (!dateValue) return null;
  if (typeof dateValue === 'object' && dateValue.seconds) return new Date(dateValue.seconds * 1000);
  if (typeof dateValue === 'string') { const d = new Date(dateValue); return isNaN(d.getTime()) ? null : d; }
  return null;
};

const formatDate = (dateValue) => {
  const date = parseDate(dateValue);
  if (!date) return '';
  if (isToday(date)) return 'Hoy';
  if (isTomorrow(date)) return 'Mañana';
  return format(date, 'd MMM', { locale: es });
};

const isOverdue = (dateValue) => {
  const date = parseDate(dateValue);
  if (!date) return false;
  return isPast(date) && !isToday(date);
};

const getDateColor = (dateValue, estado) => {
  if (estado === 'finalizado') return 'text-gray-400';
  const date = parseDate(dateValue);
  if (!date) return 'text-gray-400';
  if (isOverdue(dateValue)) return 'text-red-500';
  if (isToday(date)) return 'text-green-600';
  if (isTomorrow(date)) return 'text-orange-500';
  return 'text-gray-500';
};

const RECURRENCE_LABELS = {
  daily: 'Diaria',
  weekdays: 'L-V',
  weekly: 'Semanal',
  biweekly: 'Quincenal',
  monthly: 'Mensual',
  quarterly: 'Trimestral',
};

const RECURRENCE_OPTIONS = [
  { value: 'none', label: 'Sin repetición' },
  { value: 'daily', label: 'Diaria' },
  { value: 'weekdays', label: 'Días laborales (L-V)' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'biweekly', label: 'Quincenal' },
  { value: 'monthly', label: 'Mensual' },
  { value: 'quarterly', label: 'Trimestral' },
];

// ── Extractor de subtareas asignadas al usuario ──
const extractUserSubtasks = (tasks, userEmail) => {
  const subtaskItems = [];
  const walk = (subtareas, parentTaskId, projectId, folderId, listId, parentRecurrence, path = []) => {
    if (!subtareas) return;
    for (const sub of subtareas) {
      const currentPath = [...path, sub.id];
      if (sub.asignados?.includes(userEmail)) {
        subtaskItems.push({
          ...sub,
          estado: sub.completado ? 'finalizado' : (sub.estado || 'planificacion'),
          prioridad: sub.prioridad || 'media',
          asignados: sub.asignados || [],
          _isSubtask: true,
          _parentTaskId: parentTaskId,
          _subtaskPath: currentPath,
          _projectId: projectId,
          _folderId: folderId,
          _listId: listId,
          _parentRecurrence: parentRecurrence,
        });
      }
      walk(sub.subtareas, parentTaskId, projectId, folderId, listId, parentRecurrence, currentPath);
    }
  };
  for (const task of tasks) {
    walk(task.subtareas, task.id, task.projectId, task.folderId, task.listId, task.recurrence);
  }
  return subtaskItems;
};

// ── Tab Bar ──
const TabBar = ({ activeView, setActiveView }) => {
  const tabs = [
    { id: 'lista', label: 'Lista', icon: FiList },
    { id: 'tablero', label: 'Tablero', icon: FiGrid },
    { id: 'calendario', label: 'Calendario', icon: CalendarDays },
    { id: 'notas', label: 'Notas', icon: FiMapPin },
  ];
  return (
    <div className="flex items-center gap-0.5 border-b border-gray-200 px-6 mt-1">
      {tabs.map(tab => (
        <button
          key={tab.id}
          onClick={() => setActiveView(tab.id)}
          className={`flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium border-b-2 transition-colors -mb-px ${activeView === tab.id
            ? 'border-blue-600 text-blue-600'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
        >
          <tab.icon className="w-3.5 h-3.5" />
          {tab.label}
        </button>
      ))}
    </div>
  );
};

// ── Avatar ──
const UserAvatar = ({ email, users, currentUser, size = 'w-6 h-6' }) => {
  const isMe = currentUser?.email === email;
  const found = users?.find(u => u.email === email);
  const displayName = isMe ? (currentUser.displayName || found?.displayName || email) : (found?.displayName || email);
  const photoURL = isMe ? (currentUser.photoURL || found?.photoURL) : found?.photoURL;

  return (
    <div className={`${size} rounded-full flex items-center justify-center overflow-hidden flex-shrink-0`} title={displayName}>
      {photoURL ? (
        <img src={photoURL} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-purple-500 to-indigo-600 text-white text-[10px] flex items-center justify-center font-bold">
          {(displayName || '?').charAt(0).toUpperCase()}
        </div>
      )}
    </div>
  );
};

// ── Completion Circle (Asana-style) ──
const CompletionCircle = ({ estado, onClick, size = 16 }) => {
  const isComplete = estado === 'finalizado';
  const isInProgress = estado === 'en-curso' || estado === 'en_curso' || estado === 'en-progreso';

  return (
    <button
      onClick={onClick}
      className={`group/check flex-shrink-0 rounded-full border-2 transition-all duration-200 flex items-center justify-center
        ${isComplete
          ? 'bg-green-500 border-green-500 text-white'
          : isInProgress
            ? 'border-blue-400 hover:border-blue-500 bg-white hover:bg-blue-50'
            : 'border-gray-300 hover:border-gray-400 bg-white hover:bg-gray-50'
        }`}
      style={{ width: size, height: size }}
      title={isComplete ? 'Marcar como pendiente' : 'Marcar como completada'}
    >
      {isComplete && (
        <svg width={size - 6} height={size - 6} viewBox="0 0 12 12" fill="none">
          <path d="M2.5 6L5 8.5L9.5 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
      {isInProgress && <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />}
      {!isComplete && !isInProgress && (
        <svg width={size - 6} height={size - 6} viewBox="0 0 12 12" fill="none" className="opacity-0 group-hover/check:opacity-100 transition-opacity">
          <path d="M2.5 6L5 8.5L9.5 4" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
};

// ── Recurrence Badge ──
const RecurrenceBadge = ({ recurrence, onClick }) => {
  if (!recurrence || recurrence === 'none') return null;
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1 text-[10px] bg-blue-50 text-blue-600 px-1.5 py-[2px] rounded-full font-medium hover:bg-blue-100 transition-colors flex-shrink-0"
      title={`Tarea recurrente: ${RECURRENCE_LABELS[recurrence] || recurrence}`}
    >
      <Repeat size={10} />
      {RECURRENCE_LABELS[recurrence] || recurrence}
    </button>
  );
};

// ── Recurrence Popover ──
const RecurrencePopover = ({ currentValue, onSelect, onClose, position }) => {
  useEffect(() => {
    const handleClick = (e) => {
      if (!e.target.closest('.recurrence-popover')) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  return (
    <div
      className="recurrence-popover fixed z-50 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 w-56"
      style={{ top: position.top, left: position.left }}
    >
      <div className="px-3 py-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Repetir tarea</div>
      {RECURRENCE_OPTIONS.map(opt => (
        <button
          key={opt.value}
          onClick={() => { onSelect(opt.value); onClose(); }}
          className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${currentValue === opt.value
            ? 'bg-blue-50 text-blue-700 font-medium'
            : 'text-gray-700 hover:bg-gray-50'
            }`}
        >
          {opt.value !== 'none' && <Repeat size={13} className="text-blue-500" />}
          {opt.value === 'none' && <X size={13} className="text-gray-400" />}
          {opt.label}
          {currentValue === opt.value && <FiCheckCircle size={13} className="ml-auto text-blue-600" />}
        </button>
      ))}
    </div>
  );
};

// ── Inline Editable Cell ──
const InlineEditableCell = ({ value, onSave, isCompleted }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => { setEditValue(value); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const handleBlur = () => {
    setEditing(false);
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== value) onSave(trimmed);
    else setEditValue(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); inputRef.current?.blur(); }
    if (e.key === 'Escape') { setEditValue(value); setEditing(false); }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        className="w-full text-[13px] bg-white border border-blue-400 rounded px-1.5 py-0.5 outline-none ring-2 ring-blue-100 text-gray-900"
      />
    );
  }

  return (
    <span
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      className={`text-[13px] cursor-text hover:bg-gray-100 rounded px-1.5 py-0.5 -mx-1.5 truncate block transition-colors ${isCompleted ? 'text-gray-400 line-through' : 'text-gray-900'
        }`}
      title="Clic para editar"
    >
      {value}
    </span>
  );
};

// ── Project Location Pill ──
const ProjectLocationPill = ({ task, projectsMap, onClick }) => {
  const projectName = projectsMap[task.projectId || task._projectId] || '';
  if (!projectName) return <span className="text-[11px] text-gray-300">--</span>;

  return (
    <button
      onClick={(e) => { e.stopPropagation(); if (onClick) onClick(task); }}
      className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-blue-600 bg-gray-50 hover:bg-blue-50 px-2 py-0.5 rounded-full transition-colors truncate max-w-full"
      title={projectName}
    >
      <FiFolder size={10} className="flex-shrink-0" />
      <span className="truncate">{projectName}</span>
    </button>
  );
};

// ── VISTA LISTA (Asana-like enhanced) ──
const ListaView = ({ tasks, users, currentUser, onTaskClick, onSaveTitle, onToggleComplete, onChangeRecurrence, projectsMap, groupBy, filter, onBulkUpdate, onBulkDelete }) => {
  const [recurrencePopover, setRecurrencePopover] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [selectedTasks, setSelectedTasks] = useState(new Set());
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkPriorityOpen, setBulkPriorityOpen] = useState(false);
  const [bulkAssigneeOpen, setBulkAssigneeOpen] = useState(false);
  const [bulkDateOpen, setBulkDateOpen] = useState(false);

  const closeBulkMenus = () => { setBulkStatusOpen(false); setBulkPriorityOpen(false); setBulkAssigneeOpen(false); setBulkDateOpen(false); };

  const toggleSelect = (taskId, e) => {
    e.stopPropagation();
    setSelectedTasks(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const getSelectedItems = () => tasks.filter(t => selectedTasks.has(t._isSubtask ? `sub-${t._parentTaskId}-${t.id}` : t.id));
  const toggleGroup = (id) => setCollapsedGroups(prev => ({ ...prev, [id]: !prev[id] }));

  // Group tasks based on groupBy
  const groups = useMemo(() => {
    let filtered = tasks;

    // Apply filter
    if (filter === 'pending') filtered = tasks.filter(t => t.estado !== 'finalizado');
    else if (filter === 'completed') filtered = tasks.filter(t => t.estado === 'finalizado');
    else if (filter === 'overdue') filtered = tasks.filter(t => isOverdue(t.fechaVencimiento) && t.estado !== 'finalizado');
    else if (filter === 'recurring') filtered = tasks.filter(t => t.recurrence && t.recurrence !== 'none');
    else if (filter === 'today') filtered = tasks.filter(t => { const d = parseDate(t.fechaVencimiento); return d && isToday(d) && t.estado !== 'finalizado'; });

    if (groupBy === 'project') {
      const grouped = {};
      filtered.forEach(task => {
        const pid = task.projectId || task._projectId || 'sin-proyecto';
        const name = projectsMap[pid] || 'Sin proyecto';
        if (!grouped[pid]) grouped[pid] = { id: pid, name, tasks: [] };
        grouped[pid].tasks.push(task);
      });
      return Object.values(grouped);
    }

    if (groupBy === 'status') {
      const statusGroups = {
        'overdue': { id: 'overdue', name: 'Vencidas', color: 'text-red-600', tasks: [] },
        'today': { id: 'today', name: 'Para hoy', color: 'text-green-600', tasks: [] },
        'upcoming': { id: 'upcoming', name: 'Próximas', color: 'text-blue-600', tasks: [] },
        'no-date': { id: 'no-date', name: 'Sin fecha', color: 'text-gray-500', tasks: [] },
        'completed': { id: 'completed', name: 'Completadas', color: 'text-green-500', tasks: [] },
      };
      filtered.forEach(task => {
        if (task.estado === 'finalizado') { statusGroups['completed'].tasks.push(task); return; }
        const date = parseDate(task.fechaVencimiento);
        if (!date) { statusGroups['no-date'].tasks.push(task); return; }
        if (isOverdue(task.fechaVencimiento)) { statusGroups['overdue'].tasks.push(task); return; }
        if (isToday(date)) { statusGroups['today'].tasks.push(task); return; }
        statusGroups['upcoming'].tasks.push(task);
      });
      return Object.values(statusGroups).filter(g => g.tasks.length > 0);
    }

    // Default: flat (no groups)
    return [{ id: 'all', name: null, tasks: filtered }];
  }, [tasks, groupBy, filter, projectsMap]);

  if (tasks.length === 0) return <EmptyState />;

  return (
    <div className="px-2 sm:px-4 pt-0 pb-8">
      {/* Column Header */}
      <div className="flex items-center gap-0 px-2 py-2 border-b border-gray-200 text-[12px] font-medium text-gray-500 tracking-normal sticky top-0 bg-white z-10">
        <div className="w-7 shrink-0" />
        <div className="w-8 shrink-0" />
        <div className="w-7 shrink-0" />
        <div className="flex-1 min-w-0 pl-1">Nombre de la tarea</div>
        <div className="w-[120px] shrink-0 text-center hidden sm:block">Fecha límite</div>
        <div className="w-[60px] shrink-0 text-center hidden md:block">Asignado</div>
        <div className="w-[100px] shrink-0 text-center hidden sm:block">Estado</div>
        <div className="w-[140px] shrink-0 hidden lg:block pl-2">Proyecto</div>
        <div className="w-[80px] shrink-0 text-center hidden xl:block">Repetir</div>
      </div>

      {/* Groups */}
      {groups.map(group => (
        <div key={group.id}>
          {/* Group Header */}
          {group.name && (
            <div
              onClick={() => toggleGroup(group.id)}
              className="flex items-center gap-2 px-2 py-2 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100 sticky top-[37px] bg-white z-[5]"
            >
              <ChevronDown
                size={16}
                className={`text-gray-400 transition-transform duration-200 ${collapsedGroups[group.id] ? '-rotate-90' : ''}`}
              />
              <span className={`text-[14px] font-bold ${group.color || 'text-gray-800'}`}>
                {group.name}
              </span>
              <span className="text-[12px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full font-medium">
                {group.tasks.length}
              </span>
            </div>
          )}

          {/* Task Rows */}
          {!collapsedGroups[group.id] && group.tasks.map((task, index) => {
            const dateColor = getDateColor(task.fechaVencimiento, task.estado);
            const recurrence = task.recurrence || task._parentRecurrence;
            const taskKey = task._isSubtask ? `sub-${task._parentTaskId}-${task.id}` : task.id;
            const isSelected = selectedTasks.has(taskKey);

            return (
              <div
                key={taskKey}
                onClick={() => onTaskClick(task)}
                className={`flex items-center gap-0 px-2 py-[6px] border-b border-gray-100 cursor-pointer transition-colors group
                  ${isSelected ? 'bg-blue-50/60' : task.estado === 'finalizado' ? 'bg-gray-50/50 hover:bg-gray-100/50' : 'hover:bg-blue-50/40'}`}
                style={{ minHeight: 38 }}
              >
                {/* Checkbox */}
                <div className="w-7 shrink-0 flex items-center justify-center">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => toggleSelect(taskKey, e)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                    style={isSelected ? { opacity: 1 } : {}}
                  />
                </div>

                {/* Row number */}
                <div className="w-8 text-center shrink-0 text-[11px] text-gray-300 tabular-nums font-mono">
                  {index + 1}
                </div>

                {/* Completion toggle */}
                <div className="w-7 shrink-0 flex items-center justify-center">
                  <CompletionCircle
                    estado={task.estado}
                    onClick={(e) => { e.stopPropagation(); onToggleComplete(task); }}
                    size={18}
                  />
                </div>

                {/* Task name */}
                <div className="flex-1 min-w-0 flex items-center gap-1.5 pl-1">
                  <InlineEditableCell
                    value={task.titulo}
                    isCompleted={task.estado === 'finalizado'}
                    onSave={(newTitle) => onSaveTitle(task, newTitle)}
                  />
                  {task._isSubtask && (
                    <span className="text-[9px] bg-purple-100 text-purple-600 px-1.5 py-[1px] rounded-full font-medium shrink-0">Sub</span>
                  )}
                  {/* Recurrence badge inline on mobile */}
                  {recurrence && recurrence !== 'none' && (
                    <span className="xl:hidden flex items-center gap-0.5 text-[9px] text-blue-500 flex-shrink-0">
                      <Repeat size={9} />
                    </span>
                  )}
                </div>

                {/* Date */}
                <div className="w-[120px] shrink-0 flex items-center justify-center gap-1 hidden sm:flex">
                  {task.fechaVencimiento ? (
                    <>
                      <FiCalendar size={11} className={dateColor} />
                      <span className={`text-[12px] font-medium ${dateColor}`}>
                        {formatDate(task.fechaVencimiento)}
                      </span>
                    </>
                  ) : (
                    <span className="text-[11px] text-gray-300">--</span>
                  )}
                </div>

                {/* Assignee */}
                <div className="w-[60px] shrink-0 flex justify-center hidden md:flex">
                  <div className="flex -space-x-1.5">
                    {task.asignados?.slice(0, 2).map((email, i) => (
                      <div key={i} className="border-2 border-white rounded-full">
                        <UserAvatar email={email} users={users} currentUser={currentUser} size="w-6 h-6" />
                      </div>
                    ))}
                    {(!task.asignados || task.asignados.length === 0) && (
                      <div className="w-6 h-6 rounded-full border border-dashed border-gray-300 flex items-center justify-center">
                        <FiUser size={11} className="text-gray-300" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Status */}
                <div className="w-[100px] shrink-0 flex justify-center hidden sm:flex">
                  <span className={`text-[11px] px-2 py-[3px] rounded-full font-medium ${task.estado === 'finalizado' ? 'bg-green-100 text-green-700' :
                    task.estado === 'en-curso' || task.estado === 'en_curso' || task.estado === 'en-progreso' ? 'bg-blue-100 text-blue-700' :
                      isOverdue(task.fechaVencimiento) ? 'bg-red-50 text-red-600' : 'bg-gray-100 text-gray-500'
                    }`}>
                    {task.estado === 'finalizado' ? 'Completado' :
                      task.estado === 'en-curso' || task.estado === 'en_curso' || task.estado === 'en-progreso' ? 'En curso' :
                        isOverdue(task.fechaVencimiento) ? 'Vencida' : 'Pendiente'}
                  </span>
                </div>

                {/* Project */}
                <div className="w-[140px] shrink-0 hidden lg:block pl-2">
                  <ProjectLocationPill task={task} projectsMap={projectsMap} />
                </div>

                {/* Recurrence */}
                <div className="w-[80px] shrink-0 flex justify-center hidden xl:flex">
                  {!task._isSubtask ? (
                    <RecurrenceBadge
                      recurrence={recurrence}
                      onClick={(e) => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setRecurrencePopover({
                          task,
                          position: { top: rect.bottom + 4, left: rect.left - 100 }
                        });
                      }}
                    />
                  ) : recurrence && recurrence !== 'none' ? (
                    <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                      <Repeat size={9} /> Hereda
                    </span>
                  ) : null}
                  {!task._isSubtask && (!recurrence || recurrence === 'none') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const rect = e.currentTarget.getBoundingClientRect();
                        setRecurrencePopover({
                          task,
                          position: { top: rect.bottom + 4, left: rect.left - 100 }
                        });
                      }}
                      className="p-1 text-gray-300 hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-all rounded hover:bg-blue-50"
                      title="Configurar repetición"
                    >
                      <Repeat size={13} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {/* Recurrence Popover */}
      {recurrencePopover && (
        <RecurrencePopover
          currentValue={recurrencePopover.task.recurrence || 'none'}
          position={recurrencePopover.position}
          onSelect={(value) => onChangeRecurrence(recurrencePopover.task, value)}
          onClose={() => setRecurrencePopover(null)}
        />
      )}

      {/* Bulk Actions Toolbar */}
      {selectedTasks.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-1 bg-gray-900 text-white px-4 py-2 rounded-xl shadow-2xl border border-gray-700">
          <span className="text-sm font-medium text-blue-400 mr-2">
            {selectedTasks.size} seleccionada{selectedTasks.size > 1 ? 's' : ''}
          </span>
          <div className="w-px h-5 bg-gray-600" />

          {/* Status */}
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setBulkStatusOpen(!bulkStatusOpen); setBulkPriorityOpen(false); setBulkAssigneeOpen(false); setBulkDateOpen(false); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg hover:bg-gray-700 transition-colors"
            >
              <FiCheckCircle size={14} /> Estado
            </button>
            {bulkStatusOpen && (
              <div className="absolute bottom-full mb-2 left-0 bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[160px]">
                {[
                  { key: 'planificacion', label: 'Planificación', color: 'bg-gray-400' },
                  { key: 'en-curso', label: 'En Curso', color: 'bg-blue-500' },
                  { key: 'finalizado', label: 'Finalizado', color: 'bg-green-500' },
                ].map(s => (
                  <button key={s.key} onClick={() => { onBulkUpdate(getSelectedItems(), { estado: s.key }); setSelectedTasks(new Set()); closeBulkMenus(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                    <div className={`w-2.5 h-2.5 rounded-full ${s.color}`} />
                    {s.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Priority */}
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setBulkPriorityOpen(!bulkPriorityOpen); setBulkStatusOpen(false); setBulkAssigneeOpen(false); setBulkDateOpen(false); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg hover:bg-gray-700 transition-colors"
            >
              <FiFlag size={14} /> Prioridad
            </button>
            {bulkPriorityOpen && (
              <div className="absolute bottom-full mb-2 left-0 bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[140px]">
                {[
                  { key: 'alta', label: 'Alta', color: 'bg-red-500' },
                  { key: 'media', label: 'Media', color: 'bg-yellow-500' },
                  { key: 'baja', label: 'Baja', color: 'bg-green-500' },
                ].map(p => (
                  <button key={p.key} onClick={() => { onBulkUpdate(getSelectedItems(), { prioridad: p.key }); setSelectedTasks(new Set()); closeBulkMenus(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                    <div className={`w-2.5 h-2.5 rounded-full ${p.color}`} />
                    {p.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Assignee */}
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setBulkAssigneeOpen(!bulkAssigneeOpen); setBulkStatusOpen(false); setBulkPriorityOpen(false); setBulkDateOpen(false); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg hover:bg-gray-700 transition-colors"
            >
              <FiUserPlus size={14} /> Asignar
            </button>
            {bulkAssigneeOpen && (
              <div className="absolute bottom-full mb-2 left-0 bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[200px] max-h-[200px] overflow-y-auto">
                <button onClick={() => { onBulkUpdate(getSelectedItems(), { asignados: [] }); setSelectedTasks(new Set()); closeBulkMenus(); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 transition-colors">
                  <FiUser size={14} /> Sin asignar
                </button>
                {users.map(u => (
                  <button key={u.email} onClick={() => { onBulkUpdate(getSelectedItems(), { asignados: [u.email] }); setSelectedTasks(new Set()); closeBulkMenus(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors">
                    {u.photoURL ? (
                      <img src={u.photoURL} alt="" className="w-5 h-5 rounded-full" />
                    ) : (
                      <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center">
                        {(u.displayName || u.email).charAt(0).toUpperCase()}
                      </div>
                    )}
                    <span className="truncate">{u.displayName || u.email}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Date */}
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setBulkDateOpen(!bulkDateOpen); setBulkStatusOpen(false); setBulkPriorityOpen(false); setBulkAssigneeOpen(false); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg hover:bg-gray-700 transition-colors"
            >
              <FiCalendar size={14} /> Fecha
            </button>
            {bulkDateOpen && (
              <div className="absolute bottom-full mb-2 left-0 bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[160px]">
                {[
                  { label: 'Hoy', days: 0 },
                  { label: 'Mañana', days: 1 },
                  { label: 'Próxima semana', days: 7 },
                  { label: 'En 2 semanas', days: 14 },
                  { label: 'En 1 mes', days: 30 },
                  { label: 'Sin fecha', days: null },
                ].map(d => (
                  <button key={d.label} onClick={() => {
                    const newDate = d.days === null ? null : (() => { const dt = new Date(); dt.setDate(dt.getDate() + d.days); return dt.toISOString(); })();
                    onBulkUpdate(getSelectedItems(), { fechaVencimiento: newDate });
                    setSelectedTasks(new Set()); closeBulkMenus();
                  }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors text-left">
                    {d.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="w-px h-5 bg-gray-600" />

          <button onClick={() => { onBulkDelete(getSelectedItems()); setSelectedTasks(new Set()); closeBulkMenus(); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-red-400 hover:bg-red-900/30 transition-colors">
            <FiTrash2 size={14} /> Eliminar
          </button>

          <button onClick={() => { setSelectedTasks(new Set()); closeBulkMenus(); }}
            className="flex items-center justify-center w-7 h-7 rounded-lg hover:bg-gray-700 transition-colors ml-1">
            <FiX size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

// ── VISTA CALENDARIO ──
const CalendarioView = ({ tasks, onTaskClick, onAddTask }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const calendarData = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const date = new Date(year, month, d);
      const dayTasks = tasks.filter(t => {
        const td = parseDate(t.fechaVencimiento);
        return td && td.getFullYear() === year && td.getMonth() === month && td.getDate() === d;
      });
      days.push({ date, day: d, tasks: dayTasks });
    }
    return days;
  }, [currentMonth, tasks]);

  return (
    <div className="flex-1 p-4 sm:p-6 overflow-auto">
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors text-sm">&larr;</button>
            <button onClick={() => setCurrentMonth(new Date())} className="px-3 py-1 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">Hoy</button>
            <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors text-sm">&rarr;</button>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 capitalize">
            {format(currentMonth, 'MMMM yyyy', { locale: es })}
          </h3>
        </div>

        <div className="grid grid-cols-7 border-b border-gray-100">
          {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(d => (
            <div key={d} className="text-center py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {calendarData.map((cell, i) => {
            if (!cell) return <div key={`empty-${i}`} className="min-h-[100px] border-b border-r border-gray-50 bg-gray-50/30" />;
            const isTodayCell = isToday(cell.date);
            return (
              <div key={i} className={`group min-h-[100px] border-b border-r border-gray-50 p-1.5 relative ${isTodayCell ? 'bg-blue-50/40' : ''}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className={`text-xs font-medium ${isTodayCell ? 'w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center' : 'text-gray-500 px-1'}`}>
                    {cell.day}
                  </div>
                  {onAddTask && (
                    <button
                      onClick={() => onAddTask(cell.date)}
                      title="Nueva tarea este día"
                      className="w-5 h-5 rounded flex items-center justify-center text-gray-300 hover:bg-blue-100 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-all"
                    >
                      <Plus size={12} />
                    </button>
                  )}
                </div>
                <div className="space-y-0.5">
                  {cell.tasks.slice(0, 3).map(task => (
                    <div
                      key={task.id}
                      onClick={() => onTaskClick(task)}
                      className={`text-[10px] px-1.5 py-0.5 rounded cursor-pointer truncate transition-colors flex items-center gap-1 ${task.estado === 'finalizado' ? 'bg-green-100 text-green-600 line-through' :
                        task.prioridad === 'alta' ? 'bg-red-100 text-red-700 hover:bg-red-200' :
                          task.prioridad === 'media' ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' :
                            'bg-green-100 text-green-700 hover:bg-green-200'
                        }`}
                    >
                      {task.recurrence && task.recurrence !== 'none' && <Repeat size={8} />}
                      {task.titulo}
                    </div>
                  ))}
                  {cell.tasks.length > 3 && (
                    <div className="text-[10px] text-gray-400 px-1">+{cell.tasks.length - 3} más</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ── Empty State ──
const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-20">
    <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-4">
      <FiCheckCircle size={32} className="text-gray-300" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-1">No tienes tareas asignadas</h3>
    <p className="text-gray-500 text-sm text-center max-w-md">
      Cuando te asignen tareas en cualquier proyecto, aparecerán aquí organizadas por fecha.
    </p>
  </div>
);

// ── MAIN ──
const MyTasks = () => {
  const { user } = useAuth();
  const { setSelectedTaskId, setShowTaskDrawer, setSelectedSubtaskPath, users, setMyTasks, projects } = useTask();
  const [rawTasks, setRawTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('lista');
  const [groupBy, setGroupBy] = useState('none'); // none, project, status
  const [filter, setFilter] = useState('all'); // all, pending, completed, overdue, recurring
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Modal de creación de tareas, compartido por Lista, Tablero y Calendario.
  // `defaults` son los valores que hereda según desde dónde se abrió
  // (la columna del tablero o el día del calendario).
  const [createModal, setCreateModal] = useState({ open: false, defaults: {} });
  const openCreateModal = useCallback((defaults = {}) => setCreateModal({ open: true, defaults }), []);
  const closeCreateModal = useCallback(() => setCreateModal(prev => ({ ...prev, open: false })), []);

  // Build projects name lookup
  const projectsMap = useMemo(() => {
    const map = {};
    if (projects) {
      projects.forEach(p => { map[p.id] = p.nombre || p.name || 'Proyecto'; });
    }
    return map;
  }, [projects]);

  // Migration (localStorage para no repetir en cada sesión del navegador)
  useEffect(() => {
    if (!user?.uid) return;
    const migrationKey = `allAsignados_migrated_${user.uid}`;
    if (localStorage.getItem(migrationKey)) return;
    migrateAllAsignados(user.uid).then((count) => {
      if (count > 0) console.log(`Migración: ${count} tareas actualizadas`);
      localStorage.setItem(migrationKey, 'true');
    });
  }, [user?.uid]);

  // Migración workspaceId: backfill para que collectionGroup filtre por workspace
  useEffect(() => {
    if (!user?.uid) return;
    const migrationKey = `workspaceId_migrated_${user.uid}`;
    if (localStorage.getItem(migrationKey)) return;
    migrateWorkspaceId(user.uid).then((count) => {
      if (count > 0) console.log(`Migración workspaceId: ${count} tareas actualizadas`);
      localStorage.setItem(migrationKey, 'true');
    });
  }, [user?.uid]);

  // Subscribe to tasks
  useEffect(() => {
    if (!user?.email) { setRawTasks([]); setLoading(false); return; }
    setLoading(true);
    const unsubscribe = subscribeToMyTasks(user.email, user.workspaceId || user.uid, (tasks) => {
      setRawTasks(tasks);
      setMyTasks(tasks);
      setLoading(false);
    });
    return () => { if (unsubscribe) unsubscribe(); setMyTasks([]); };
  }, [user?.email, user?.workspaceId, user?.uid, setMyTasks]);

  // Combine tasks + subtasks
  const allItems = useMemo(() => {
    if (!user?.email) return [];
    const directTasks = rawTasks.filter(t => t.asignados?.includes(user.email));
    const subtaskItems = extractUserSubtasks(rawTasks, user.email);
    const seen = new Set();
    const merged = [];
    for (const item of [...directTasks, ...subtaskItems]) {
      if (!seen.has(item.id)) { seen.add(item.id); merged.push(item); }
    }

    // Collapse old completed recurring tasks - only keep latest completed + pending
    // Group completed tasks by titulo to find recurring duplicates
    const titleCompletedCount = {};
    merged.forEach(item => {
      if (item.estado === 'finalizado' && !item._isSubtask) {
        titleCompletedCount[item.titulo] = (titleCompletedCount[item.titulo] || 0) + 1;
      }
    });

    const collapsed = [];
    const bestCompleted = {}; // key: titulo -> latest completed task (only for titles with 2+ completed)
    for (const item of merged) {
      const isCompleted = item.estado === 'finalizado';
      const hasDuplicates = (titleCompletedCount[item.titulo] || 0) > 1;
      if (isCompleted && hasDuplicates && !item._isSubtask) {
        const key = item.titulo;
        const existing = bestCompleted[key];
        if (!existing) {
          bestCompleted[key] = item;
        } else {
          const existingDate = parseDate(existing.fechaVencimiento);
          const currentDate = parseDate(item.fechaVencimiento);
          if (currentDate && existingDate && currentDate > existingDate) {
            bestCompleted[key] = item;
          }
        }
      } else {
        collapsed.push(item);
      }
    }
    // Add only the latest completed instance per group
    Object.values(bestCompleted).forEach(t => collapsed.push(t));

    // Apply search
    let filtered = collapsed;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = collapsed.filter(t => t.titulo?.toLowerCase().includes(q));
    }

    // Sort priority: 1) Today (not completed), 2) Overdue (not completed), 3) Pending future, 4) Completed
    const getSortGroup = (item) => {
      if (item.estado === 'finalizado') return 4; // Completed last
      const d = parseDate(item.fechaVencimiento);
      if (!d) return 3; // No date = pending
      if (isToday(d)) return 1; // Today first
      if (isPast(d) && !isToday(d)) return 2; // Overdue second
      return 3; // Future pending
    };

    return filtered.sort((a, b) => {
      const ga = getSortGroup(a);
      const gb = getSortGroup(b);
      if (ga !== gb) return ga - gb;
      // Within same group, sort by date
      const da = parseDate(a.fechaVencimiento);
      const db2 = parseDate(b.fechaVencimiento);
      if (!da && !db2) return 0;
      if (!da) return 1;
      if (!db2) return -1;
      return da.getTime() - db2.getTime();
    });
  }, [rawTasks, user?.email, searchQuery]);

  // Stats
  const stats = useMemo(() => {
    const total = allItems.length;
    const completed = allItems.filter(t => t.estado === 'finalizado').length;
    const overdue = allItems.filter(t => isOverdue(t.fechaVencimiento) && t.estado !== 'finalizado').length;
    const recurring = allItems.filter(t => t.recurrence && t.recurrence !== 'none').length;
    const todayCount = allItems.filter(t => { const d = parseDate(t.fechaVencimiento); return d && isToday(d) && t.estado !== 'finalizado'; }).length;
    return { total, completed, overdue, recurring, todayCount };
  }, [allItems]);

  const handleTaskClick = (item) => {
    if (item._isSubtask) {
      setSelectedTaskId(item._parentTaskId);
      setSelectedSubtaskPath(item._subtaskPath);
    } else {
      setSelectedTaskId(item.id);
      setSelectedSubtaskPath([]);
    }
    setShowTaskDrawer(true);
  };

  const handleSaveTitle = useCallback(async (item, newTitle) => {
    if (!user?.uid) return;
    try {
      if (item._isSubtask) {
        await updateSubtask(user.uid, item._projectId, item._folderId, item._listId, item._parentTaskId, item._subtaskPath, { titulo: newTitle });
      } else {
        await updateTask(user.uid, item.projectId, item.folderId, item.listId, item.id, { titulo: newTitle });
      }
    } catch (err) { console.error('Error al guardar título:', err); }
  }, [user?.uid]);

  const handleToggleComplete = useCallback(async (item) => {
    if (!user?.uid) return;
    const newStatus = item.estado === 'finalizado' ? 'planificacion' : 'finalizado';
    try {
      if (item._isSubtask) {
        await updateSubtask(user.uid, item._projectId, item._folderId, item._listId, item._parentTaskId, item._subtaskPath, { estado: newStatus });
      } else {
        await updateTask(user.uid, item.projectId, item.folderId, item.listId, item.id, { estado: newStatus });
      }
      if (newStatus === 'finalizado') {
        if (item.recurrence && item.recurrence !== 'none') {
          toast.success(`Tarea completada. Se creará la siguiente (${RECURRENCE_LABELS[item.recurrence]})`);
        } else {
          toast.success('Tarea completada');
        }
      }
    } catch (err) {
      console.error('Error al cambiar estado:', err);
      toast.error('Error al actualizar estado');
    }
  }, [user?.uid]);

  const handleChangeRecurrence = useCallback(async (item, recurrence) => {
    if (!user?.uid || item._isSubtask) return;
    try {
      await updateTask(user.uid, item.projectId, item.folderId, item.listId, item.id, { recurrence });
      toast.success(recurrence === 'none' ? 'Repetición eliminada' : `Tarea repetirá ${RECURRENCE_LABELS[recurrence]}`);
    } catch (err) {
      console.error('Error al cambiar recurrencia:', err);
      toast.error('Error al configurar repetición');
    }
  }, [user?.uid]);

  // ── Bulk action handlers ──
  const handleBulkUpdate = useCallback(async (items, updates) => {
    if (!user?.uid || items.length === 0) return;
    try {
      await Promise.all(items.map(item => {
        if (item._isSubtask) {
          return updateSubtask(user.uid, item._projectId, item._folderId, item._listId, item._parentTaskId, item._subtaskPath, updates);
        }
        return updateTask(user.uid, item.projectId, item.folderId, item.listId, item.id, updates);
      }));
      toast.success(`${items.length} tarea${items.length > 1 ? 's' : ''} actualizada${items.length > 1 ? 's' : ''}`);
    } catch (err) {
      console.error('Error bulk update:', err);
      toast.error('Error al actualizar tareas');
    }
  }, [user?.uid]);

  const handleBulkDelete = useCallback(async (items) => {
    if (!user?.uid || items.length === 0) return;
    if (!window.confirm(`¿Eliminar ${items.length} tarea${items.length > 1 ? 's' : ''}? Esta acción no se puede deshacer.`)) return;
    try {
      await Promise.all(items.filter(item => !item._isSubtask).map(item =>
        deleteTask(user.uid, item.projectId, item.folderId, item.listId, item.id)
      ));
      toast.success(`${items.length} tarea${items.length > 1 ? 's' : ''} eliminada${items.length > 1 ? 's' : ''}`);
    } catch (err) {
      console.error('Error bulk delete:', err);
      toast.error('Error al eliminar tareas');
    }
  }, [user?.uid]);

  return (
    <div className="flex-1 flex flex-col bg-white h-full overflow-hidden">
      {/* Header */}
      <div className="px-4 sm:px-6 pt-4 pb-0">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-baseline gap-3">
            <h1 className="text-lg font-bold text-gray-900">Mis tareas</h1>
            <span className="text-[12px] text-gray-400">{stats.total} tareas</span>
          </div>
          <button
            onClick={() => openCreateModal()}
            className="flex items-center gap-1 text-[12px] px-3 py-1.5 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 transition-all"
          >
            <Plus size={14} /> Nueva tarea
          </button>
        </div>

        {/* Quick Stats */}
        <div className="flex items-center gap-2 mb-2 overflow-x-auto scrollbar-hide pb-1">
          <button
            onClick={() => setFilter(filter === 'all' ? 'all' : 'all')}
            className={`flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full font-medium transition-colors whitespace-nowrap ${filter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Todas {stats.total}
          </button>
          {stats.todayCount > 0 && (
            <button
              onClick={() => setFilter(filter === 'today' ? 'all' : 'today')}
              className={`flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full font-medium transition-colors whitespace-nowrap ${filter === 'today' ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
            >
              <FiClock size={10} /> Hoy {stats.todayCount}
            </button>
          )}
          {stats.overdue > 0 && (
            <button
              onClick={() => setFilter(filter === 'overdue' ? 'all' : 'overdue')}
              className={`flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full font-medium transition-colors whitespace-nowrap ${filter === 'overdue' ? 'bg-red-600 text-white' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}
            >
              Vencidas {stats.overdue}
            </button>
          )}
          {stats.recurring > 0 && (
            <button
              onClick={() => setFilter(filter === 'recurring' ? 'all' : 'recurring')}
              className={`flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full font-medium transition-colors whitespace-nowrap ${filter === 'recurring' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}
            >
              <Repeat size={10} /> Recurrentes {stats.recurring}
            </button>
          )}
          <button
            onClick={() => setFilter(filter === 'completed' ? 'all' : 'completed')}
            className={`flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full font-medium transition-colors whitespace-nowrap ${filter === 'completed' ? 'bg-green-600 text-white' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
          >
            Completadas {stats.completed}
          </button>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Search */}
          <div className="relative hidden sm:block">
            <FiSearch size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar tarea..."
              className="text-[12px] pl-7 pr-2 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 w-40 lg:w-52 bg-gray-50"
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X size={12} />
              </button>
            )}
          </div>

          {/* Group By */}
          <div className="relative hidden md:block">
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="text-[12px] px-2 py-1.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-600 focus:outline-none focus:ring-1 focus:ring-blue-400 cursor-pointer appearance-none pr-6"
            >
              <option value="none">Sin agrupar</option>
              <option value="project">Por proyecto</option>
              <option value="status">Por estado</option>
            </select>
            <ChevronDown size={12} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <TabBar activeView={activeView} setActiveView={setActiveView} />

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activeView === 'lista' ? (
          <ListaView
            tasks={allItems}
            users={users}
            currentUser={user}
            onTaskClick={handleTaskClick}
            onSaveTitle={handleSaveTitle}
            onToggleComplete={handleToggleComplete}
            onChangeRecurrence={handleChangeRecurrence}
            projectsMap={projectsMap}
            groupBy={groupBy}
            filter={filter}
            onBulkUpdate={handleBulkUpdate}
            onBulkDelete={handleBulkDelete}
          />
        ) : activeView === 'tablero' ? (
          <MyTasksBoard
            tasks={allItems}
            users={users}
            currentUser={user}
            userId={user?.uid}
            onTaskClick={handleTaskClick}
            onToggleComplete={handleToggleComplete}
            onAddTask={(column) => openCreateModal(defaultsForColumn(column))}
            projectsMap={projectsMap}
            CompletionCircle={CompletionCircle}
            UserAvatar={UserAvatar}
          />
        ) : activeView === 'notas' ? (
          <PersonalNotesView userId={user?.uid} />
        ) : (
          <CalendarioView
            tasks={allItems}
            onTaskClick={handleTaskClick}
            onAddTask={(date) => openCreateModal({ fechaVencimiento: date.toISOString() })}
          />
        )}
      </div>

      <TaskCreateModal
        open={createModal.open}
        onClose={closeCreateModal}
        user={user}
        projects={projects}
        defaults={createModal.defaults}
      />
    </div>
  );
};

// ── VISTA NOTAS PERSONALES (Premium + Chat IA + Carpetas) ──
const PersonalNotesView = ({ userId }) => {
  // ── State ──
  const [folders, setFolders] = useState([]);
  const [notes, setNotes] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null); // null = all notes
  const [selectedNote, setSelectedNote] = useState(null);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [streamingText, setStreamingText] = useState(null); // { content: '', fullContent: '' }
  const [renamingFolder, setRenamingFolder] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [collapsedFolders, setCollapsedFolders] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showFoldersPopup, setShowFoldersPopup] = useState(false);
  const [chatImage, setChatImage] = useState(null);
  const [chatDocs, setChatDocs] = useState([]); // [{name, type, content}]
  const [showPersonalize, setShowPersonalize] = useState(false);
  const [personalization, setPersonalization] = useState({ name: '', role: '', preferences: '' });
  const [chatFullscreen, setChatFullscreen] = useState(false);
  const [chatWidth, setChatWidth] = useState(45);
  const [showEbook, setShowEbook] = useState(false); // percentage
  const [selectionPopup, setSelectionPopup] = useState(null);
  const [chatSelectionPopup, setChatSelectionPopup] = useState(null); // { x, y, text }
  const [aiRefineLoading, setAiRefineLoading] = useState(false);
  const [slashMenu, setSlashMenu] = useState(null); // { x, y, filter }
  const [slashFilter, setSlashFilter] = useState('');
  const [colorDropdown, setColorDropdown] = useState(false);
  const isDraggingRef = useRef(false);
  const editorRef = useRef(null);
  const saveTimerRef = useRef(null);
  const chatEndRef = useRef(null);
  const chatInputRef = useRef(null);

  const notesPath = `users/${userId}/personal_notes`;
  const foldersPath = `users/${userId}/note_folders`;

  // Rich markdown to HTML renderer (ChatGPT-style)
  const renderMarkdown = (text) => {
    if (!text) return '';
    return text
      // Code blocks
      .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre><code>$2</code></pre>')
      // Inline code
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      // Blockquotes
      .replace(/^>(?: (.*))?$/gm, '<div class="chat-blockquote"><p>$1</p></div>')
      .replace(/<\/div>\s*<div class="chat-blockquote">/g, '')
      // Headings (#### before ### before ## before #)
      .replace(/^#{4}\s+(.+)$/gm, '<h4>$1</h4>')
      .replace(/^#{3}\s+(.+)$/gm, '<h4>$1</h4>')
      .replace(/^#{2}\s+(.+)$/gm, '<h3>$1</h3>')
      .replace(/^#{1}\s+(.+)$/gm, '<h2>$1</h2>')
      // Bold
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      // Bullets & sub-bullets (BEFORE italic)
      .replace(/^(\s*)[-*]\s+(.+)$/gm, (_, indent, content) => {
        const cls = indent.length >= 2 ? 'chat-sub-bullet' : 'chat-bullet';
        return `<div class="${cls}"><span class="bullet-dot">•</span><span>${content}</span></div>`;
      })
      // Numbered lists
      .replace(/^\s*(\d+)\.\s+(.+)$/gm, '<div class="chat-numbered"><span class="num">$1.</span><span>$2</span></div>')
      // Italic (safe)
      .replace(/(?<![*\w])\*([^*\n]+)\*(?![*\w])/g, '<em>$1</em>')
      // Horizontal rule
      .replace(/^---$/gm, '<hr />')
      // Paragraph spacing
      .replace(/\n{2,}/g, '<div class="chat-separator"></div>')
      .replace(/\n/g, '<br/>');
  };

  // Convert markdown to editor-friendly HTML (for "Insertar en nota")
  // Convert chat HTML (dark theme, flex divs) to clean editor HTML (single column, light theme)
  const cleanChatHtmlForEditor = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;

    // Convert chat-bullet and chat-sub-bullet divs to <li>
    tmp.querySelectorAll('.chat-bullet, .chat-sub-bullet, [class*="chat-bullet"], [class*="chat-sub-bullet"]').forEach(el => {
      const li = document.createElement('li');
      const spans = el.querySelectorAll('span');
      li.innerHTML = spans.length > 1 ? spans[spans.length - 1].innerHTML : el.textContent;
      if (el.className.includes('sub')) li.style.marginLeft = '24px';
      el.replaceWith(li);
    });

    // Convert chat-numbered divs to <li>
    tmp.querySelectorAll('.chat-numbered, [class*="chat-numbered"]').forEach(el => {
      const li = document.createElement('li');
      const spans = el.querySelectorAll('span');
      li.innerHTML = spans.length > 1 ? spans[spans.length - 1].innerHTML : el.textContent;
      el.replaceWith(li);
    });

    // Convert chat-blockquote to <blockquote>
    tmp.querySelectorAll('.chat-blockquote, [class*="chat-blockquote"]').forEach(el => {
      const bq = document.createElement('blockquote');
      bq.innerHTML = el.innerHTML;
      el.replaceWith(bq);
    });

    // Convert chat-separator to <br>
    tmp.querySelectorAll('.chat-separator, [class*="chat-separator"]').forEach(el => {
      el.replaceWith(document.createElement('br'));
    });

    // Remove all class attributes and dark-theme inline styles
    tmp.querySelectorAll('*').forEach(el => {
      el.removeAttribute('class');
      const style = el.getAttribute('style');
      if (style && /rgba\(255,\s*255,\s*255/.test(style)) el.removeAttribute('style');
    });

    return tmp.innerHTML;
  };

  const markdownToEditorHtml = (md) => {
    if (!md) return '';
    return md.split('\n').map(line => {
      if (line.trim() === '') return '';
      if (line.trim() === '---') return '<hr style="border:none;border-top:1px solid #e5e7eb;margin:12px 0;" />';
      if (/^#{4}\s+(.+)$/.test(line)) return `<h4>${line.replace(/^#{4}\s+/, '')}</h4>`;
      if (/^#{3}\s+(.+)$/.test(line)) return `<h4>${line.replace(/^#{3}\s+/, '')}</h4>`;
      if (/^#{2}\s+(.+)$/.test(line)) return `<h3>${line.replace(/^#{2}\s+/, '')}</h3>`;
      if (/^#{1}\s+(.+)$/.test(line)) return `<h2>${line.replace(/^#{1}\s+/, '')}</h2>`;
      // Bold conversion
      line = line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      // Italic
      line = line.replace(/(?<![*\w])\*([^*\n]+)\*(?![*\w])/g, '<em>$1</em>');
      // Sub-bullet
      if (/^\s{2,}[-*]\s+/.test(line)) return `<ul style="margin-left:24px;"><li>${line.replace(/^\s+[-*]\s+/, '')}</li></ul>`;
      // Bullet
      if (/^[-*]\s+/.test(line)) return `<li>${line.replace(/^[-*]\s+/, '')}</li>`;
      // Numbered
      if (/^\s*\d+\.\s/.test(line)) return `<li>${line.replace(/^\s*\d+\.\s+/, '')}</li>`;
      // Blockquote
      if (/^>\s?(.*)$/.test(line)) return `<blockquote>${line.replace(/^>\s?/, '')}</blockquote>`;
      return `<p>${line}</p>`;
    }).filter(Boolean).join('');
  };

  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // ── Load folders ──
  useEffect(() => {
    if (!userId) return;
    const q = query(collection(db, foldersPath), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      setFolders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [userId]);

  // ── Load personalization ──
  useEffect(() => {
    if (!userId) return;
    const unsub = onSnapshot(doc(db, `users/${userId}/preferences`, 'chat_personalization'), (snap) => {
      if (snap.exists()) setPersonalization(snap.data());
    });
    return () => unsub();
  }, [userId]);

  const savePersonalization = async (data) => {
    if (!userId) return;
    try {
      const { setDoc } = await import('firebase/firestore');
      await setDoc(doc(db, `users/${userId}/preferences`, 'chat_personalization'), data);
      setPersonalization(data);
      toast.success('Personalización guardada');
      setShowPersonalize(false);
    } catch { toast.error('Error al guardar'); }
  };

  // ── Load notes ──
  useEffect(() => {
    if (!userId) return;
    const q = query(collection(db, notesPath), orderBy('updatedAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setNotes(list);
      if (!selectedNote && list.length > 0) {
        setSelectedNote(list[0]);
        setNoteTitle(list[0].title || '');
        setNoteContent(list[0].content || '');
      }
    });
    return () => unsub();
  }, [userId]);

  // ── Load chat messages for selected note ──
  useEffect(() => {
    if (!userId || !selectedNote?.id) { setChatMessages([]); return; }
    const chatPath = `${notesPath}/${selectedNote.id}/chat_messages`;
    const q = query(collection(db, chatPath), orderBy('timestamp', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      setChatMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [userId, selectedNote?.id]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatLoading]);

  // Sync editor
  useEffect(() => {
    if (editorRef.current && selectedNote) {
      if (document.activeElement !== editorRef.current) {
        editorRef.current.innerHTML = noteContent || '';
      }
    }
  }, [selectedNote?.id]);

  // ── Folder actions ──
  const handleNewFolder = async () => {
    try {
      await addDoc(collection(db, foldersPath), {
        name: 'Nueva carpeta',
        createdAt: serverTimestamp(),
      });
    } catch { toast.error('Error al crear carpeta'); }
  };

  const handleRenameFolder = async (folderId, name) => {
    try {
      await updateDoc(doc(db, foldersPath, folderId), { name });
      setRenamingFolder(null);
    } catch { toast.error('Error al renombrar'); }
  };

  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Eliminar carpeta? Las notas se moveran a "Sin carpeta"')) return;
    try {
      // Move notes out of folder
      const folderNotes = notes.filter(n => n.folderId === folderId);
      await Promise.all(folderNotes.map(n => updateDoc(doc(db, notesPath, n.id), { folderId: null })));
      await deleteDoc(doc(db, foldersPath, folderId));
      if (selectedFolderId === folderId) setSelectedFolderId(null);
      toast.success('Carpeta eliminada');
    } catch { toast.error('Error al eliminar carpeta'); }
  };

  // ── Note actions ──
  const handleSelectNote = (note) => {
    if (selectedNote && (noteTitle !== selectedNote.title || noteContent !== selectedNote.content)) {
      doSave(selectedNote.id, noteTitle, noteContent);
    }
    setSelectedNote(note);
    setNoteTitle(note.title || '');
    setNoteContent(note.content || '');
    if (editorRef.current) editorRef.current.innerHTML = note.content || '';
  };

  const handleNewNote = async (folderId = null) => {
    try {
      const ref = await addDoc(collection(db, notesPath), {
        title: 'Nueva nota',
        content: '',
        folderId: folderId || selectedFolderId || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      const newNote = { id: ref.id, title: 'Nueva nota', content: '', folderId: folderId || selectedFolderId || null };
      setSelectedNote(newNote);
      setNoteTitle('Nueva nota');
      setNoteContent('');
      if (editorRef.current) editorRef.current.innerHTML = '';
    } catch { toast.error('Error al crear nota'); }
  };

  const doSave = async (noteId, title, content) => {
    if (!noteId) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, notesPath, noteId), { title, content, updatedAt: serverTimestamp() });
    } catch { toast.error('Error al guardar'); }
    setSaving(false);
  };

  const triggerAutoSave = useCallback((title, content) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      if (selectedNote) doSave(selectedNote.id, title, content);
    }, 1500);
  }, [selectedNote]);

  const handleTitleChange = (e) => { const val = e.target.value; setNoteTitle(val); triggerAutoSave(val, noteContent); };

  const handleEditorInput = () => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    setNoteContent(html);
    triggerAutoSave(noteTitle, html);
    // Save cursor position for later insertion
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && editorRef.current.contains(sel.anchorNode)) {
      editorCursorRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Eliminar esta nota?')) return;
    try {
      await deleteDoc(doc(db, notesPath, noteId));
      if (selectedNote?.id === noteId) {
        setSelectedNote(null); setNoteTitle(''); setNoteContent('');
        if (editorRef.current) editorRef.current.innerHTML = '';
      }
      toast.success('Nota eliminada');
    } catch { toast.error('Error al eliminar'); }
  };

  const handleMoveNote = async (noteId, newFolderId) => {
    try {
      await updateDoc(doc(db, notesPath, noteId), { folderId: newFolderId || null });
      toast.success('Nota movida');
    } catch { toast.error('Error al mover'); }
  };

  // ── Context menu blocks (right-click) ──
  const CONTEXT_BLOCKS = [
    { id: 'text', label: 'Texto', icon: 'T', action: () => exec('formatBlock', '<p>'), group: 'block' },
    { id: 'h1', label: 'Encabezado 1', icon: 'H1', action: () => exec('formatBlock', '<h1>'), group: 'block' },
    { id: 'h2', label: 'Encabezado 2', icon: 'H2', action: () => exec('formatBlock', '<h2>'), group: 'block' },
    { id: 'h3', label: 'Encabezado 3', icon: 'H3', action: () => exec('formatBlock', '<h3>'), group: 'block' },
    { id: 'h4', label: 'Encabezado 4', icon: 'H4', action: () => exec('formatBlock', '<h4>'), group: 'block' },
    { id: 'bullet', label: 'Lista con viñetas', icon: '•', action: () => exec('insertUnorderedList'), group: 'list' },
    { id: 'numbered', label: 'Lista numerada', icon: '1.', action: () => exec('insertOrderedList'), group: 'list' },
    { id: 'checklist', label: 'Lista de tareas', icon: '☑', action: () => exec('insertHTML', '<div style="display:flex;gap:8px;align-items:flex-start;margin:4px 0;"><input type="checkbox" style="margin-top:3px;accent-color:#6366f1;" /><span>Tarea</span></div>'), group: 'list' },
    { id: 'divider', label: 'Divisor', icon: '—', action: () => exec('insertHTML', '<hr style="border:none;border-top:1px solid #e5e7eb;margin:16px 0;" />'), group: 'insert' },
    { id: 'quote', label: 'Cita', icon: '"', action: () => exec('formatBlock', '<blockquote>'), group: 'insert' },
    { id: 'code', label: 'Código', icon: '</>', action: () => exec('insertHTML', '<pre style="background:#f3f4f6;border-radius:8px;padding:12px;font-family:monospace;font-size:13px;margin:8px 0;white-space:pre-wrap;"><code>código aquí</code></pre>'), group: 'insert' },
    { id: 'table', label: 'Tabla', icon: '▦', action: () => exec('insertHTML', '<table style="width:100%;border-collapse:collapse;margin:12px 0;font-size:13px;"><tr><th style="border:1px solid #e5e7eb;padding:8px;background:#f9fafb;text-align:left;">Columna 1</th><th style="border:1px solid #e5e7eb;padding:8px;background:#f9fafb;text-align:left;">Columna 2</th><th style="border:1px solid #e5e7eb;padding:8px;background:#f9fafb;text-align:left;">Columna 3</th></tr><tr><td style="border:1px solid #e5e7eb;padding:8px;"></td><td style="border:1px solid #e5e7eb;padding:8px;"></td><td style="border:1px solid #e5e7eb;padding:8px;"></td></tr><tr><td style="border:1px solid #e5e7eb;padding:8px;"></td><td style="border:1px solid #e5e7eb;padding:8px;"></td><td style="border:1px solid #e5e7eb;padding:8px;"></td></tr></table>'), group: 'insert' },
    { id: 'image', label: 'Imagen', icon: '🖼', action: () => insertImage(), group: 'media' },
    { id: 'paste-plain', label: 'Pegar sin formato', icon: '📋', action: async () => {
      try {
        const text = await navigator.clipboard.readText();
        if (text) exec('insertText', text);
      } catch { toast.error('No hay acceso al portapapeles'); }
    }, group: 'clipboard' },
  ];

  const contextRangeRef = useRef(null);

  const handleEditorContextMenu = (e) => {
    e.preventDefault();
    // Save current cursor/selection position before menu steals focus
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      contextRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
    const editorRect = editorRef.current.getBoundingClientRect();
    setSlashMenu({ x: e.clientX - editorRect.left, y: e.clientY - editorRect.top });
    setSlashFilter('');
  };

  const executeBlock = (block) => {
    setSlashMenu(null);
    setSlashFilter('');
    // Save scroll position
    const scrollParent = editorRef.current?.closest('.overflow-y-auto');
    const scrollTop = scrollParent?.scrollTop || 0;
    // Restore cursor position before executing command
    if (contextRangeRef.current && editorRef.current) {
      editorRef.current.focus({ preventScroll: true });
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(contextRangeRef.current);
    }
    block.action();
    // Restore scroll
    if (scrollParent) requestAnimationFrame(() => { scrollParent.scrollTop = scrollTop; });
  };

  // ── Editor toolbar ──
  const exec = (cmd, val = null) => {
    const scrollParent = editorRef.current?.closest('.overflow-y-auto');
    const scrollTop = scrollParent?.scrollTop || 0;
    document.execCommand(cmd, false, val);
    editorRef.current?.focus({ preventScroll: true });
    handleEditorInput();
    if (scrollParent) requestAnimationFrame(() => { scrollParent.scrollTop = scrollTop; });
  };

  const insertImage = () => {
    const input = document.createElement('input'); input.type = 'file'; input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0]; if (!file) return;
      if (file.size > 5 * 1024 * 1024) { toast.error('Imagen muy grande (max 5MB)'); return; }
      const reader = new FileReader();
      reader.onload = (ev) => exec('insertHTML', `<img src="${ev.target.result}" style="max-width:100%;border-radius:8px;margin:8px 0;" />`);
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const insertLink = () => { const url = prompt('URL del enlace:'); if (url) exec('createLink', url); };

  const editorCursorRef = useRef(null); // Save cursor position when leaving editor

  const insertHtmlToEditor = (html) => {
    if (!editorRef.current) return;
    const scrollParent = editorRef.current.closest('.overflow-y-auto');
    const scrollTop = scrollParent?.scrollTop || 0;
    editorRef.current.focus({ preventScroll: true });

    // Restore saved cursor position, or place at end
    const sel = window.getSelection();
    if (editorCursorRef.current) {
      try {
        sel.removeAllRanges();
        sel.addRange(editorCursorRef.current);
      } catch {
        sel.selectAllChildren(editorRef.current);
        sel.collapseToEnd();
      }
    } else {
      sel.selectAllChildren(editorRef.current);
      sel.collapseToEnd();
    }

    // Insert a separator + content (not replace)
    document.execCommand('insertHTML', false, '<br/>' + html);
    handleEditorInput();
    if (scrollParent) requestAnimationFrame(() => { scrollParent.scrollTop = scrollTop; });
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items; if (!items) return;
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const reader = new FileReader();
        reader.onload = (ev) => exec('insertHTML', `<img src="${ev.target.result}" style="max-width:100%;border-radius:8px;margin:8px 0;" />`);
        reader.readAsDataURL(item.getAsFile());
        return;
      }
    }
  };

  // ── Text selection popup (inline AI) ──
  const savedRangeRef = useRef(null);

  const handleEditorMouseUp = () => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.toString().trim()) { setSelectionPopup(null); return; }
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    // Save the range so we can restore it after clicking the input
    savedRangeRef.current = range.cloneRange();
    setSelectionPopup({ x: rect.left + rect.width / 2, y: rect.top - 8, text: sel.toString().trim() });
  };

  const refineSelectedText = async (instruction) => {
    if (!selectionPopup?.text) return;
    setAiRefineLoading(true);
    try {
      const { refineContent } = await import('../../../services/gemini');
      const result = await refineContent(selectionPopup.text, instruction);
      // Clean markdown artifacts
      const cleanText = result
        .replace(/\*\*(.+?)\*\*/g, '$1')
        .replace(/\*(.+?)\*/g, '$1')
        .replace(/^#{1,3}\s+/gm, '')
        .replace(/^[-*]\s+/gm, '• ')
        .trim();
      // Restore selection, delete selected text, then typewriter effect
      if (savedRangeRef.current && editorRef.current) {
        // Save scroll position
        const scrollParent = editorRef.current.closest('.overflow-y-auto') || editorRef.current.parentElement;
        const scrollTop = scrollParent?.scrollTop || 0;

        editorRef.current.focus();
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(savedRangeRef.current);
        // Delete the selected text first
        document.execCommand('insertText', false, '');

        // Restore scroll
        if (scrollParent) scrollParent.scrollTop = scrollTop;

        setAiRefineLoading(false);
        setSelectionPopup(null);
        savedRangeRef.current = null;

        // Typewriter effect - insert char by char
        const chars = cleanText.split('');
        let i = 0;
        const typeChar = () => {
          if (i < chars.length) {
            document.execCommand('insertText', false, chars[i]);
            i++;
            // Vary speed slightly for natural feel
            const delay = chars[i - 1] === '\n' ? 30 : (i % 3 === 0 ? 15 : 8);
            setTimeout(typeChar, delay);
          } else {
            handleEditorInput();
          }
        };
        typeChar();
        return; // Skip the finally block's cleanup since we already did it
      }
    } catch (err) { toast.error('Error: ' + err.message); }
    setAiRefineLoading(false);
    setSelectionPopup(null);
    savedRangeRef.current = null;
  };

  // ── Chat IA contextual ──
  const sendChatMessage = async (messageText, imageData = null) => {
    if ((!messageText?.trim() && !imageData) || !selectedNote?.id || chatLoading) return;
    const userMsg = messageText?.trim() || '(imagen)';
    setChatInput('');
    const currentImage = imageData || chatImage;
    setChatImage(null);
    setChatLoading(true);

    // Save user message to Firestore
    const chatPath = `${notesPath}/${selectedNote.id}/chat_messages`;
    try {
      await addDoc(collection(db, chatPath), {
        role: 'user', content: userMsg, timestamp: serverTimestamp(),
        ...(currentImage ? { hasImage: true } : {}),
      });
    } catch { /* optimistic */ }

    try {
      const { generateChatResponse, searchWeb, shouldSearchWeb } = await import('../../../services/gemini');

      const history = [...chatMessages, { role: 'user', content: userMsg }].map(m => ({
        role: m.role, content: m.content,
      }));

      let webSearchResults = null;
      try {
        const { needsSearch, searchQuery: sq } = await shouldSearchWeb(userMsg);
        if (needsSearch && sq) webSearchResults = await searchWeb(sq);
      } catch { /* skip */ }

      const noteText = stripHtml(noteContent);
      const noteContext = [{
        id: 'current-note', title: noteTitle || 'Nota actual', content: noteText, category: 'nota-personal',
      }];

      const personCtx = personalization.name || personalization.role || personalization.preferences
        ? `\n\nSOBRE EL USUARIO:\n- Nombre: ${personalization.name || 'No especificado'}\n- Rol: ${personalization.role || 'No especificado'}\n- Preferencias: ${personalization.preferences || 'Ninguna'}\n`
        : '';

      // Build documents context
      const docsCtx = chatDocs.filter(d => d.type === 'text').length > 0
        ? '\n\nDOCUMENTOS CARGADOS POR EL USUARIO (usa esta información para responder):\n' +
          chatDocs.filter(d => d.type === 'text').map(d => `--- ${d.name} ---\n${d.content}`).join('\n\n')
        : '';

      // Collect image docs for vision
      const docImages = chatDocs.filter(d => d.type === 'image').map(d => d.content);
      const allImages = currentImage ? [currentImage, ...docImages] : docImages.length > 0 ? docImages : null;

      const aiSettings = {
        companyName: 'Chat IA',
        customPrompt: `Eres un asistente inteligente tipo ChatGPT. Respondes preguntas, investigas, generas contenido, desarrollas ideas, creas planes, temarios, estrategias, y cualquier cosa que el usuario pida.${personCtx}

El usuario tiene una nota abierta titulada "${noteTitle}" con este contenido como referencia (puede o no ser relevante):
---
${noteText.substring(0, 3000)}
---${docsCtx}

INSTRUCCIONES:
- Responde como ChatGPT: completo, detallado, util y conversacional
- Si el usuario pregunta algo nuevo que no tiene que ver con la nota, responde normalmente
- Da respuestas largas y completas cuando el tema lo amerite
- USA FORMATO RICO: **negritas** para conceptos clave, ## para títulos, ### para subtítulos, listas con - o numeradas
- USA EMOJIS relevantes en títulos y puntos importantes (🔥 🎯 💡 🚀 📌 ✅ ⚡ 💰 📊 🎓 👉 ⭐)
- Si investigaste en web, cita las fuentes
- Responde siempre en español
- Se proactivo: sugiere siguientes pasos e ideas adicionales
- Si el usuario envia una imagen o documento, analízalo y responde sobre él
- Si hay documentos cargados, USALOS como base de conocimiento para responder con precisión`,
      };

      const aiResponse = await generateChatResponse(history, noteContext, aiSettings, allImages?.[0] || null, [], webSearchResults);

      // Typewriter effect - stream the response word by word
      setChatLoading(false);
      const words = aiResponse.split(/(\s+)/); // split keeping spaces
      let displayed = '';
      setStreamingText({ content: '', fullContent: aiResponse, hasWebSearch: !!webSearchResults });

      for (let i = 0; i < words.length; i++) {
        displayed += words[i];
        setStreamingText(prev => prev ? { ...prev, content: displayed } : null);
        // Speed: faster for spaces, slower for words
        if (words[i].trim()) {
          await new Promise(r => setTimeout(r, 18));
        }
      }

      setStreamingText(null);

      // Save AI response to Firestore (will appear via onSnapshot)
      await addDoc(collection(db, chatPath), {
        role: 'assistant', content: aiResponse, timestamp: serverTimestamp(),
        ...(webSearchResults ? { hasWebSearch: true } : {}),
      });
    } catch (err) {
      console.error('Chat AI error:', err);
      setStreamingText(null);
      await addDoc(collection(db, `${notesPath}/${selectedNote.id}/chat_messages`), {
        role: 'assistant', content: 'Error al procesar tu mensaje. Intenta de nuevo.', timestamp: serverTimestamp(),
      });
    }
    setChatLoading(false);
    setTimeout(() => chatInputRef.current?.focus(), 100);
  };

  const clearChat = async () => {
    if (!selectedNote?.id || !window.confirm('Limpiar historial del chat?')) return;
    try {
      const chatPath = `${notesPath}/${selectedNote.id}/chat_messages`;
      const snap = await getDocs(collection(db, chatPath));
      await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
      toast.success('Chat limpiado');
    } catch { toast.error('Error al limpiar chat'); }
  };

  // ── Upload documents to chat context ──
  const handleDocUpload = () => {
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.accept = '.pdf,.txt,.csv,.json,.md,.html,.doc,.docx,image/*';
    inp.multiple = true;
    inp.onchange = async (e) => {
      const files = Array.from(e.target.files);
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} muy grande (max 10MB)`); continue; }

        if (file.type.startsWith('image/')) {
          // Images: convert to base64 for vision
          const reader = new FileReader();
          reader.onload = (ev) => {
            setChatDocs(prev => [...prev, { name: file.name, type: 'image', content: ev.target.result, size: file.size }]);
            toast.success(`Imagen ${file.name} cargada`);
          };
          reader.readAsDataURL(file);
        } else {
          // Text-based files: read as text
          try {
            const text = await file.text();
            const truncated = text.substring(0, 15000); // Limit context size
            setChatDocs(prev => [...prev, { name: file.name, type: 'text', content: truncated, size: file.size }]);
            toast.success(`${file.name} cargado (${(file.size / 1024).toFixed(0)}KB)`);
          } catch {
            toast.error(`No se pudo leer ${file.name}`);
          }
        }
      }
    };
    inp.click();
  };

  // ── Filter notes by folder and search ──
  const filteredNotes = useMemo(() => {
    let filtered = notes;
    if (selectedFolderId) filtered = notes.filter(n => n.folderId === selectedFolderId);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(n =>
        (n.title || '').toLowerCase().includes(q) || stripHtml(n.content || '').toLowerCase().includes(q)
      );
    }
    return filtered;
  }, [notes, selectedFolderId, searchQuery]);

  const unfolderedNotes = useMemo(() => notes.filter(n => !n.folderId), [notes]);

  // ── Render ──
  return (
    <div className="flex flex-1 overflow-hidden" style={{ height: 'calc(100vh - 180px)' }}>
      {/* ══ SIDEBAR (collapsible) ══ */}
      {sidebarOpen && (
        <div className="w-56 border-r border-gray-200 bg-gray-50/50 flex flex-col shrink-0">
          {/* Header */}
          <div className="p-2 border-b border-gray-100 flex items-center justify-between gap-1">
            <div className="flex items-center gap-1.5">
              <button onClick={() => setSidebarOpen(false)} className="p-1 hover:bg-gray-200 rounded text-gray-400 transition-colors" title="Ocultar sidebar">
                <FiChevronRight size={12} className="rotate-180" />
              </button>
              <span className="text-[11px] font-semibold text-gray-700">Notas</span>
            </div>
            <div className="flex items-center gap-0.5">
              <button onClick={() => setShowFoldersPopup(true)} className="p-1 hover:bg-gray-200 rounded text-gray-500 transition-colors" title="Carpetas">
                <FiFolder size={12} />
              </button>
              <button onClick={() => handleNewNote()} className="p-1 hover:bg-blue-50 rounded text-blue-600 transition-colors" title="Nueva nota">
                <FiPlus size={13} />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="px-2 py-1 border-b border-gray-100">
            <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-md px-1.5 py-0.5">
              <FiSearch size={10} className="text-gray-400 shrink-0" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Buscar..."
                className="text-[10px] bg-transparent border-none focus:outline-none flex-1 text-gray-600 placeholder-gray-300" />
              {searchQuery && <button onClick={() => setSearchQuery('')} className="text-gray-300 hover:text-gray-500"><FiX size={9} /></button>}
            </div>
          </div>

          {/* Folder selector */}
          <div className="px-2 py-1 border-b border-gray-100 flex-shrink-0">
            <select value={selectedFolderId || ''} onChange={e => setSelectedFolderId(e.target.value || null)}
              className="w-full text-[10px] border border-gray-200 rounded-md px-2 py-1 text-gray-600 bg-white focus:outline-none focus:ring-1 focus:ring-blue-300 cursor-pointer font-medium">
              <option value="">Todas las notas ({notes.length})</option>
              {folders.map(f => (
                <option key={f.id} value={f.id}>{f.name} ({notes.filter(n => n.folderId === f.id).length})</option>
              ))}
            </select>
          </div>

          {/* Notes list */}
          <div className="flex-1 overflow-y-auto">
            {filteredNotes.length === 0 ? (
              <div className="p-4 text-center text-gray-400 text-xs">
                {searchQuery ? 'Sin resultados' : 'Sin notas'}
                {!searchQuery && <button onClick={() => handleNewNote()} className="mt-2 block mx-auto text-blue-500 text-xs font-medium">Crear nota</button>}
              </div>
            ) : filteredNotes.map(note => (
              <NoteItem key={note.id} note={note} selected={selectedNote?.id === note.id} onSelect={handleSelectNote} onDelete={handleDelete} stripHtml={stripHtml} />
            ))}
          </div>
        </div>
      )}

      {/* Sidebar toggle when collapsed */}
      {!sidebarOpen && (
        <div className="w-10 border-r border-gray-200 bg-gray-50/50 flex flex-col items-center py-2 gap-2 shrink-0">
          <button onClick={() => setSidebarOpen(true)} className="p-1.5 hover:bg-gray-200 rounded-lg text-gray-500 transition-colors" title="Mostrar notas">
            <FiChevronRight size={14} />
          </button>
          <button onClick={() => handleNewNote()} className="p-1.5 hover:bg-blue-50 rounded-lg text-blue-600 transition-colors" title="Nueva nota">
            <FiPlus size={14} />
          </button>
          <button onClick={() => setShowFoldersPopup(true)} className="p-1.5 hover:bg-gray-200 rounded-lg text-gray-500 transition-colors" title="Carpetas">
            <FiFolder size={14} />
          </button>
        </div>
      )}

      {/* ══ FOLDERS POPUP (grid 4 cols) ══ */}
      {showFoldersPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center" onClick={() => setShowFoldersPopup(false)}
          style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(8px)' }}>
          <div className="w-[600px] max-w-[92vw] bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-gray-800">Mis Carpetas</h3>
                <p className="text-[10px] text-gray-400 mt-0.5">{folders.length} carpeta{folders.length !== 1 ? 's' : ''} · {notes.length} nota{notes.length !== 1 ? 's' : ''}</p>
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={handleNewFolder} className="px-3 py-1.5 rounded-lg text-[11px] font-medium flex items-center gap-1 text-white" style={{ background: '#1e293b' }}>
                  <FiPlus size={11} /> Nueva carpeta
                </button>
                <button onClick={() => setShowFoldersPopup(false)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400"><FiX size={15} /></button>
              </div>
            </div>
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-4 gap-2.5">
                {/* All notes card */}
                <button onClick={() => { setSelectedFolderId(null); setShowFoldersPopup(false); }}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-all text-center ${!selectedFolderId ? 'bg-blue-50 border-2 border-blue-300 shadow-sm' : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100 hover:border-gray-200'}`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${!selectedFolderId ? 'bg-blue-200' : 'bg-gray-200'}`}>
                    <FiList size={18} className={!selectedFolderId ? 'text-blue-700' : 'text-gray-500'} />
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-gray-700 truncate w-full">Todas</p>
                    <p className="text-[9px] text-gray-400">{notes.length}</p>
                  </div>
                </button>

                {/* Folder cards */}
                {folders.map(folder => {
                  const count = notes.filter(n => n.folderId === folder.id).length;
                  const isActive = selectedFolderId === folder.id;
                  return (
                    <div key={folder.id} className="relative group">
                      <button onClick={() => { setSelectedFolderId(folder.id); setShowFoldersPopup(false); }}
                        className={`w-full flex flex-col items-center gap-2 p-3 rounded-xl transition-all text-center ${isActive ? 'bg-indigo-50 border-2 border-indigo-300 shadow-sm' : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100 hover:border-gray-200'}`}>
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isActive ? 'bg-indigo-200' : 'bg-slate-200'}`}>
                          <FiFolder size={18} className={isActive ? 'text-indigo-700' : 'text-slate-500'} />
                        </div>
                        <div className="w-full">
                          {renamingFolder === folder.id ? (
                            <input autoFocus value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
                              onBlur={() => handleRenameFolder(folder.id, newFolderName)}
                              onKeyDown={e => { if (e.key === 'Enter') handleRenameFolder(folder.id, newFolderName); if (e.key === 'Escape') setRenamingFolder(null); }}
                              onClick={e => e.stopPropagation()}
                              className="text-[11px] bg-white border border-blue-300 rounded px-1.5 py-0.5 w-full focus:outline-none font-medium text-center" />
                          ) : (
                            <>
                              <p className="text-[11px] font-semibold text-gray-700 truncate">{folder.name}</p>
                              <p className="text-[9px] text-gray-400">{count} nota{count !== 1 ? 's' : ''}</p>
                            </>
                          )}
                        </div>
                      </button>
                      {/* Hover actions */}
                      <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={(e) => { e.stopPropagation(); setRenamingFolder(folder.id); setNewFolderName(folder.name); }}
                          className="p-1 bg-white/90 rounded shadow-sm hover:bg-gray-100 text-gray-400" title="Renombrar"><FiEdit3 size={9} /></button>
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id); }}
                          className="p-1 bg-white/90 rounded shadow-sm hover:bg-red-50 text-gray-400 hover:text-red-500" title="Eliminar"><FiTrash2 size={9} /></button>
                      </div>
                    </div>
                  );
                })}

                {/* Add folder card */}
                <button onClick={handleNewFolder}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl border-2 border-dashed border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all text-center">
                  <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                    <FiPlus size={18} className="text-gray-400" />
                  </div>
                  <p className="text-[10px] text-gray-400 font-medium">Agregar</p>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ EDITOR ══ */}
      <div className="flex-1 bg-white flex flex-col min-w-0">
        {selectedNote ? (
          <>
            {/* Title bar */}
            <div className="px-5 py-2.5 border-b border-gray-100 flex items-center justify-between gap-3">
              <input type="text" value={noteTitle} onChange={handleTitleChange} placeholder="Titulo de la nota"
                className="text-base font-bold text-gray-800 bg-transparent border-none focus:outline-none flex-1" />
              <div className="flex items-center gap-1 shrink-0">
                <span className="text-[10px] text-gray-400 mr-1">{saving ? 'Guardando...' : 'Auto-guardado'}</span>
                {/* Move to folder */}
                {folders.length > 0 && (
                  <select value={selectedNote.folderId || ''} onChange={e => handleMoveNote(selectedNote.id, e.target.value || null)}
                    className="text-[10px] border border-gray-200 rounded-md px-1.5 py-1 text-gray-500 focus:outline-none bg-white cursor-pointer">
                    <option value="">Sin carpeta</option>
                    {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                )}
                <button onClick={() => setShowEbook(true)}
                  className="px-3 py-1.5 rounded-lg transition-all text-[11px] font-medium flex items-center gap-1.5 border border-gray-300 text-gray-600 hover:bg-gray-50"
                  title="Exportar como documento">
                  <FiBook size={13} />
                  Exportar
                </button>
                <button onClick={() => setShowChat(true)}
                  className="px-3 py-1.5 rounded-lg transition-all text-[11px] font-medium flex items-center gap-1.5 text-white shadow-sm hover:shadow-md"
                  style={{ background: '#1e293b' }}
                  title="Chat IA de investigacion">
                  <FiMessageCircle size={13} />
                  Chat IA
                  {chatMessages.length > 0 && <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />}
                </button>
              </div>
            </div>

            {/* Toolbar */}
            <div className="px-3 py-1.5 border-b border-gray-100 flex items-center gap-0.5 flex-wrap bg-gray-50/50">
              <button onClick={() => exec('bold')} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors" title="Negrita"><FiBold size={13} /></button>
              <button onClick={() => exec('italic')} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors" title="Cursiva"><FiItalic size={13} /></button>
              <button onClick={() => exec('underline')} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors" title="Subrayado"><FiUnderline size={13} /></button>
              <div className="w-px h-4 bg-gray-300 mx-0.5" />
              <button onClick={() => exec('formatBlock', '<h2>')} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors text-[10px] font-bold" title="Titulo">H2</button>
              <button onClick={() => exec('formatBlock', '<h3>')} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors text-[10px] font-bold" title="Subtitulo">H3</button>
              <div className="w-px h-4 bg-gray-300 mx-0.5" />
              <button onClick={() => exec('insertUnorderedList')} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors" title="Lista"><FiList size={13} /></button>
              <button onClick={() => exec('insertOrderedList')} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors text-[10px] font-medium" title="Lista numerada">1.</button>
              <div className="w-px h-4 bg-gray-300 mx-0.5" />
              <button onClick={insertLink} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors" title="Enlace"><FiLink size={13} /></button>
              <button onClick={insertImage} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors" title="Imagen"><FiImage size={13} /></button>
              <button onClick={() => exec('formatBlock', '<blockquote>')} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors text-[12px]" title="Cita">"</button>
              <button onClick={() => exec('insertHTML', '<hr style="border:none;border-top:1px solid #e5e7eb;margin:12px 0;" />')} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors" title="Linea"><FiMinus size={13} /></button>
              <div className="w-px h-4 bg-gray-300 mx-0.5" />
              {/* Color dropdown */}
              <div className="relative">
                <button onClick={() => setColorDropdown(!colorDropdown)} className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors flex items-center gap-0.5" title="Color">
                  <span className="text-[10px] font-bold">A</span>
                  <div className="w-3.5 h-1 rounded-sm" style={{ background: 'linear-gradient(90deg, #ea4335, #fbbc04, #34a853, #1a73e8, #9334e6)' }} />
                </button>
                {colorDropdown && (
                  <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 p-3 z-50 w-[220px]" onMouseLeave={() => setColorDropdown(false)}>
                    <div className="text-[10px] font-semibold text-gray-400 uppercase mb-1.5">Color del texto</div>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {[
                        { c: 'inherit', label: 'Default', bg: '#374151' },
                        { c: '#6b7280', label: 'Gris', bg: '#6b7280' },
                        { c: '#92400e', label: 'Marrón', bg: '#92400e' },
                        { c: '#ea580c', label: 'Naranja', bg: '#ea580c' },
                        { c: '#ca8a04', label: 'Amarillo', bg: '#ca8a04' },
                        { c: '#16a34a', label: 'Verde', bg: '#16a34a' },
                        { c: '#2563eb', label: 'Azul', bg: '#2563eb' },
                        { c: '#7c3aed', label: 'Morado', bg: '#7c3aed' },
                        { c: '#db2777', label: 'Rosa', bg: '#db2777' },
                        { c: '#dc2626', label: 'Rojo', bg: '#dc2626' },
                      ].map(({ c, label, bg }) => (
                        <button key={c} onClick={() => { exec('foreColor', c === 'inherit' ? '#374151' : c); setColorDropdown(false); }}
                          className="w-6 h-6 rounded-md border border-gray-200 hover:scale-110 transition-transform flex items-center justify-center" title={label}>
                          <span className="font-bold text-[11px]" style={{ color: bg }}>A</span>
                        </button>
                      ))}
                    </div>
                    <div className="text-[10px] font-semibold text-gray-400 uppercase mb-1.5">Color de fondo</div>
                    <div className="flex flex-wrap gap-1">
                      {[
                        { c: 'transparent', label: 'Sin fondo', bg: '#fff', border: true },
                        { c: '#f3f4f6', label: 'Gris', bg: '#f3f4f6' },
                        { c: '#fef3c7', label: 'Amarillo', bg: '#fef3c7' },
                        { c: '#fed7aa', label: 'Naranja', bg: '#fed7aa' },
                        { c: '#fecaca', label: 'Rojo', bg: '#fecaca' },
                        { c: '#bbf7d0', label: 'Verde', bg: '#bbf7d0' },
                        { c: '#bfdbfe', label: 'Azul', bg: '#bfdbfe' },
                        { c: '#ddd6fe', label: 'Morado', bg: '#ddd6fe' },
                        { c: '#fbcfe8', label: 'Rosa', bg: '#fbcfe8' },
                        { c: '#d6d3d1', label: 'Marrón', bg: '#d6d3d1' },
                      ].map(({ c, label, bg, border }) => (
                        <button key={c} onClick={() => { exec('hiliteColor', c); setColorDropdown(false); }}
                          className={`w-6 h-6 rounded-md hover:scale-110 transition-transform ${border ? 'border border-gray-300' : ''}`}
                          style={{ backgroundColor: bg }} title={label} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="w-px h-4 bg-gray-300 mx-0.5" />
              <button onClick={() => exec('removeFormat')} className="p-1.5 rounded hover:bg-gray-200 text-gray-400 transition-colors" title="Limpiar formato"><FiX size={12} /></button>
            </div>

            {/* Editor + inline Chat */}
            <div className="flex-1 flex overflow-hidden">
              {/* Rich text editor */}
              <div className="flex-1 overflow-y-auto min-w-0 relative" onClick={() => slashMenu && setSlashMenu(null)}>
                <div ref={editorRef} contentEditable onInput={handleEditorInput} onPaste={handlePaste} onMouseUp={handleEditorMouseUp}
                  onContextMenu={handleEditorContextMenu} suppressContentEditableWarning
                  onBlur={() => {
                    const sel = window.getSelection();
                    if (sel && sel.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
                      editorCursorRef.current = sel.getRangeAt(0).cloneRange();
                    }
                  }}
                  className="min-h-full p-6 text-sm text-gray-700 leading-relaxed focus:outline-none"
                  style={{ wordBreak: 'break-word' }}
                  data-placeholder="Escribe tu nota aqui... Clic derecho para insertar bloques. Selecciona texto para IA." />

                {/* ── Right-click context menu ── */}
                {slashMenu && (
                  <div className="absolute bg-white rounded-xl shadow-2xl border border-gray-200 py-1 z-50 w-[230px] max-h-[400px] overflow-y-auto"
                    style={{ left: Math.min(slashMenu.x, 300), top: slashMenu.y }}
                    onMouseDown={e => e.preventDefault()}>
                    {/* Search filter */}
                    <div className="px-3 py-1.5">
                      <input type="text" value={slashFilter} onChange={e => setSlashFilter(e.target.value.toLowerCase())}
                        placeholder="Buscar bloques..." autoFocus
                        className="w-full text-[12px] px-2 py-1 rounded-md border border-gray-200 focus:outline-none focus:border-indigo-300 bg-gray-50" />
                    </div>
                    {/* Bloques básicos */}
                    <div className="px-3 pt-1 pb-0.5 text-[9px] font-semibold text-gray-400 uppercase">Bloques</div>
                    {CONTEXT_BLOCKS.filter(b => b.group === 'block' && (!slashFilter || b.label.toLowerCase().includes(slashFilter))).map(block => (
                      <button key={block.id} onClick={() => executeBlock(block)}
                        className="w-full px-3 py-1.5 flex items-center gap-2.5 hover:bg-indigo-50 transition-colors text-left">
                        <span className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center text-[11px] font-bold text-gray-500 shrink-0">{block.icon}</span>
                        <span className="text-[13px] text-gray-700">{block.label}</span>
                      </button>
                    ))}
                    {/* Listas */}
                    {CONTEXT_BLOCKS.filter(b => b.group === 'list' && (!slashFilter || b.label.toLowerCase().includes(slashFilter))).length > 0 && (
                      <>
                        <div className="px-3 pt-2 pb-0.5 text-[9px] font-semibold text-gray-400 uppercase border-t border-gray-100 mt-1">Listas</div>
                        {CONTEXT_BLOCKS.filter(b => b.group === 'list' && (!slashFilter || b.label.toLowerCase().includes(slashFilter))).map(block => (
                          <button key={block.id} onClick={() => executeBlock(block)}
                            className="w-full px-3 py-1.5 flex items-center gap-2.5 hover:bg-indigo-50 transition-colors text-left">
                            <span className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center text-[11px] font-bold text-gray-500 shrink-0">{block.icon}</span>
                            <span className="text-[13px] text-gray-700">{block.label}</span>
                          </button>
                        ))}
                      </>
                    )}
                    {/* Insertar */}
                    {CONTEXT_BLOCKS.filter(b => (b.group === 'insert' || b.group === 'media') && (!slashFilter || b.label.toLowerCase().includes(slashFilter))).length > 0 && (
                      <>
                        <div className="px-3 pt-2 pb-0.5 text-[9px] font-semibold text-gray-400 uppercase border-t border-gray-100 mt-1">Insertar</div>
                        {CONTEXT_BLOCKS.filter(b => (b.group === 'insert' || b.group === 'media') && (!slashFilter || b.label.toLowerCase().includes(slashFilter))).map(block => (
                          <button key={block.id} onClick={() => executeBlock(block)}
                            className="w-full px-3 py-1.5 flex items-center gap-2.5 hover:bg-indigo-50 transition-colors text-left">
                            <span className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center text-[11px] font-bold text-gray-500 shrink-0">{block.icon}</span>
                            <span className="text-[13px] text-gray-700">{block.label}</span>
                          </button>
                        ))}
                      </>
                    )}
                    {/* Portapapeles */}
                    {CONTEXT_BLOCKS.filter(b => b.group === 'clipboard' && (!slashFilter || b.label.toLowerCase().includes(slashFilter))).length > 0 && (
                      <>
                        <div className="px-3 pt-2 pb-0.5 text-[9px] font-semibold text-gray-400 uppercase border-t border-gray-100 mt-1">Portapapeles</div>
                        {CONTEXT_BLOCKS.filter(b => b.group === 'clipboard' && (!slashFilter || b.label.toLowerCase().includes(slashFilter))).map(block => (
                          <button key={block.id} onClick={() => executeBlock(block)}
                            className="w-full px-3 py-1.5 flex items-center gap-2.5 hover:bg-indigo-50 transition-colors text-left">
                            <span className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center text-[11px] font-bold text-gray-500 shrink-0">{block.icon}</span>
                            <span className="text-[13px] text-gray-700">{block.label}</span>
                          </button>
                        ))}
                      </>
                    )}
                    {CONTEXT_BLOCKS.filter(b => !slashFilter || b.label.toLowerCase().includes(slashFilter)).length === 0 && (
                      <div className="px-3 py-3 text-[12px] text-gray-400 text-center">Sin resultados</div>
                    )}
                  </div>
                )}
              </div>

              {/* ══ CHAT IA (fixed right panel, full height) ══ */}
              {showChat && !chatFullscreen && (
                <>
                  {/* Spacer to push editor content */}
                  <div style={{ width: `${chatWidth}vw` }} className="shrink-0" />
                  {/* Fixed panel */}
                  <div className="fixed top-0 right-0 bottom-0 z-[60] flex" style={{ width: `${chatWidth}vw` }}>
                  {/* Draggable divider */}
                  <div className="w-1.5 shrink-0 cursor-col-resize hover:bg-blue-400 bg-gray-700 transition-colors active:bg-blue-500"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      isDraggingRef.current = true;
                      const startX = e.clientX;
                      const startWidth = chatWidth;
                      const onMove = (me) => {
                        const delta = startX - me.clientX;
                        const newPct = Math.max(25, Math.min(65, startWidth + (delta / window.innerWidth) * 100));
                        setChatWidth(newPct);
                      };
                      const onUp = () => { isDraggingRef.current = false; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                      document.addEventListener('mousemove', onMove);
                      document.addEventListener('mouseup', onUp);
                    }}
                  />
                  <div className="flex-1 flex flex-col overflow-hidden min-h-0" style={{ background: '#212121' }}>
                  {/* Header */}
                  <div className="px-4 py-2.5 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center">
                        <FiZap size={14} className="text-white" />
                      </div>
                      <span className="text-[13px] font-medium text-white/90">HingenIA Chat</span>
                      <span className="text-[10px] text-white/30">· {noteTitle?.substring(0, 20) || 'Nota'}...</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setShowPersonalize(true)} className="p-1.5 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors relative" title="Cerebro del Asistente">
                        <span className="text-[14px]">🧠</span>
                        {(chatDocs.length > 0 || personalization.preferences) && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-indigo-500 rounded-full" />}
                      </button>
                      <button onClick={() => setChatFullscreen(!chatFullscreen)} className="p-1.5 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors" title={chatFullscreen ? 'Mitad de pantalla' : 'Pantalla completa'}>
                        {chatFullscreen ? <FiMinimize2 size={13} /> : <FiMaximize2 size={13} />}
                      </button>
                      {chatMessages.length > 0 && (
                        <button onClick={clearChat} className="p-1.5 rounded-lg text-white/40 hover:text-red-400 hover:bg-white/10 transition-colors" title="Nuevo chat">
                          <FiTrash2 size={13} />
                        </button>
                      )}
                      <button onClick={() => setShowChat(false)} className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors">
                        <FiX size={15} />
                      </button>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto px-4 relative" onMouseUp={() => {
                    const sel = window.getSelection();
                    if (!sel || sel.isCollapsed || !sel.toString().trim()) { setChatSelectionPopup(null); return; }
                    const text = sel.toString().trim();
                    if (text.length < 3) { setChatSelectionPopup(null); return; }
                    const range = sel.getRangeAt(0);
                    const rect = range.getBoundingClientRect();
                    // Capture HTML of selection (with formatting)
                    const frag = range.cloneContents();
                    const tmp = document.createElement('div');
                    tmp.appendChild(frag);
                    const html = tmp.innerHTML;
                    setChatSelectionPopup({ x: rect.left + rect.width / 2, y: rect.top - 8, text, html });
                  }} onClick={(e) => { if (!window.getSelection()?.toString().trim()) setChatSelectionPopup(null); }}>
                    {/* Floating selection popup */}
                    {chatSelectionPopup && (
                      <div className="fixed z-[80] flex items-center gap-1 bg-[#1a1a2e] border border-white/15 rounded-xl shadow-2xl px-1.5 py-1"
                        style={{ left: Math.min(chatSelectionPopup.x - 80, window.innerWidth - 200), top: chatSelectionPopup.y - 40 }}
                        onMouseDown={e => e.preventDefault()}>
                        <button onClick={() => {
                          insertHtmlToEditor(cleanChatHtmlForEditor(chatSelectionPopup.html));
                          toast.success('Insertado en la nota');
                          setChatSelectionPopup(null);
                          window.getSelection()?.removeAllRanges();
                        }} className="px-2.5 py-1.5 text-[11px] text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors flex items-center gap-1.5">
                          <FiPlus size={11} /> Insertar
                        </button>
                        <div className="w-px h-4 bg-white/10" />
                        <button onClick={() => {
                          // Copy as rich HTML to clipboard (so paste keeps format)
                          const cleaned = cleanChatHtmlForEditor(chatSelectionPopup.html);
                          const blob = new Blob([cleaned], { type: 'text/html' });
                          const textBlob = new Blob([chatSelectionPopup.text], { type: 'text/plain' });
                          navigator.clipboard.write([new ClipboardItem({ 'text/html': blob, 'text/plain': textBlob })]).then(() => {
                            toast.success('Copiado con formato');
                          }).catch(() => {
                            navigator.clipboard.writeText(chatSelectionPopup.text);
                            toast.success('Copiado');
                          });
                          setChatSelectionPopup(null);
                          window.getSelection()?.removeAllRanges();
                        }} className="px-2.5 py-1.5 text-[11px] text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors flex items-center gap-1.5">
                          <FiCopy size={11} /> Copiar
                        </button>
                      </div>
                    )}
                    <div className={`${chatFullscreen ? 'max-w-[720px]' : 'max-w-full'} mx-auto py-4 space-y-6 px-2`}>
                      {chatMessages.length === 0 && !chatLoading && (
                        <div className="text-center py-24">
                          <div className="w-12 h-12 rounded-full bg-white/10 mx-auto mb-4 flex items-center justify-center">
                            <FiZap size={22} className="text-white/60" />
                          </div>
                          <h4 className="text-lg font-medium text-white/80">Como puedo ayudarte?</h4>
                          <p className="text-sm text-white/30 mt-2 max-w-md mx-auto">Pregunta lo que quieras. Tengo contexto de tu nota y puedo buscar en internet.</p>
                          <div className="flex flex-wrap justify-center gap-2 mt-6">
                            {[
                              { label: 'Genera un temario completo', msg: 'Genera un temario completo y estructurado con módulos y subtemas detallados.' },
                              { label: 'Investiga sobre este tema', msg: 'Investiga en internet sobre este tema y dame información relevante actualizada con fuentes.' },
                              { label: 'Dame un plan de accion', msg: 'Crea un plan de acción detallado paso a paso para implementar esto.' },
                              { label: 'Ideas para expandir', msg: 'Dame ideas creativas e innovadoras para expandir este proyecto.' },
                            ].map(a => (
                              <button key={a.label} onClick={() => sendChatMessage(a.msg)}
                                className="text-[12px] px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-white/60 hover:bg-white/10 hover:text-white/80 transition-all">
                                {a.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {chatMessages.map((msg, i) => (
                        <div key={msg.id || i} className={msg.role === 'user' ? 'flex justify-end' : ''}>
                          {msg.role === 'assistant' ? (
                            <div className="flex gap-3">
                              <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center shrink-0 mt-0.5">
                                <FiZap size={13} className="text-white/70" />
                              </div>
                              <div className="flex-1 min-w-0">
                                {msg.hasWebSearch && (
                                  <div className="text-[10px] text-blue-400 mb-2 flex items-center gap-1">
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/></svg>
                                    Busco en internet
                                  </div>
                                )}
                                <div className="text-[14px] text-white/85 leading-relaxed chat-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} />
                                <div className="flex gap-3 mt-3">
                                  <button onClick={() => {
                                    insertHtmlToEditor(markdownToEditorHtml(msg.content));
                                    toast.success('Insertado en la nota');
                                  }}
                                    className="text-[11px] text-white/30 hover:text-white/60 flex items-center gap-1 transition-colors">
                                    <FiPlus size={11} /> Insertar en nota
                                  </button>
                                  <button onClick={() => { navigator.clipboard.writeText(msg.content); toast.success('Copiado'); }}
                                    className="text-[11px] text-white/30 hover:text-white/60 flex items-center gap-1 transition-colors">
                                    <FiCopy size={11} /> Copiar
                                  </button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="inline-block max-w-[80%] bg-[#2f2f2f] text-white/90 rounded-2xl px-4 py-2.5 text-[14px]">
                              {msg.hasImage && <div className="text-[10px] text-blue-400 mb-1 flex items-center gap-1"><FiImage size={10} /> Imagen adjunta</div>}
                              {msg.content}
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Streaming message (typewriter effect) */}
                      {streamingText && (
                        <div className="flex gap-3">
                          <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center shrink-0 mt-0.5">
                            <FiZap size={13} className="text-white/70" />
                          </div>
                          <div className="flex-1 min-w-0">
                            {streamingText.hasWebSearch && (
                              <div className="text-[10px] text-blue-400 mb-2 flex items-center gap-1">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/></svg>
                                Busqué en internet
                              </div>
                            )}
                            <div className="text-[14px] text-white/85 leading-relaxed chat-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(streamingText.content) }} />
                            <span className="inline-block w-0.5 h-4 bg-white/60 animate-pulse ml-0.5 align-middle" />
                          </div>
                        </div>
                      )}

                      {chatLoading && !streamingText && (
                        <div className="flex gap-3">
                          <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                            <FiZap size={13} className="text-white/70" />
                          </div>
                          <div className="flex items-center gap-2 py-2">
                            <div className="flex gap-1">
                              <div className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                              <div className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                              <div className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                            </div>
                          </div>
                        </div>
                      )}
                      <div ref={chatEndRef} />
                    </div>
                  </div>

                  {/* Input area (ChatGPT style) */}
                  <div className="px-4 pb-4 pt-2 shrink-0">
                    <div className={`${chatFullscreen ? 'max-w-[720px]' : 'max-w-full'} mx-auto`}>
                      {/* Docs loaded indicator */}
                      {chatDocs.length > 0 && (
                        <div className="mb-2 flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/20 rounded-xl px-3 py-1.5">
                          <span className="text-[12px]">🧠</span>
                          <span className="text-[11px] text-indigo-300/70 flex-1">{chatDocs.length} documento{chatDocs.length > 1 ? 's' : ''} en memoria</span>
                          <button onClick={() => setShowPersonalize(true)} className="text-[10px] text-indigo-400 hover:text-indigo-300">Gestionar</button>
                        </div>
                      )}
                      {chatImage && (
                        <div className="mb-2 flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2">
                          <img src={chatImage} alt="" className="w-10 h-10 rounded-lg object-cover" />
                          <span className="text-[11px] text-white/40 flex-1">Imagen lista para enviar</span>
                          <button onClick={() => setChatImage(null)} className="text-white/30 hover:text-white/60"><FiX size={14} /></button>
                        </div>
                      )}
                      <div className="flex items-end gap-2 bg-[#2f2f2f] rounded-2xl border border-white/10 px-3 py-2">
                        {/* Image upload */}
                        <button onClick={() => {
                          const inp = document.createElement('input'); inp.type = 'file'; inp.accept = 'image/*';
                          inp.onchange = (e) => {
                            const file = e.target.files[0]; if (!file) return;
                            const reader = new FileReader();
                            reader.onload = (ev) => setChatImage(ev.target.result);
                            reader.readAsDataURL(file);
                          };
                          inp.click();
                        }} className="p-1.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/5 transition-colors shrink-0" title="Adjuntar imagen">
                          <FiImage size={16} />
                        </button>
                        <input ref={chatInputRef} value={chatInput} onChange={e => setChatInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(chatInput); } }}
                          onPaste={e => {
                            const items = e.clipboardData?.items;
                            if (items) for (const item of items) {
                              if (item.type.startsWith('image/')) {
                                e.preventDefault();
                                const reader = new FileReader();
                                reader.onload = (ev) => setChatImage(ev.target.result);
                                reader.readAsDataURL(item.getAsFile());
                                return;
                              }
                            }
                          }}
                          placeholder="Pregunta lo que quieras..."
                          className="flex-1 bg-transparent text-[14px] text-white/90 placeholder-white/25 py-1.5 focus:outline-none resize-none"
                          disabled={chatLoading} autoFocus />
                        <button onClick={() => sendChatMessage(chatInput)} disabled={chatLoading || (!chatInput.trim() && !chatImage)}
                          className="p-1.5 rounded-lg transition-all shrink-0"
                          style={{ background: (chatInput.trim() || chatImage) && !chatLoading ? 'white' : 'transparent', color: (chatInput.trim() || chatImage) && !chatLoading ? '#212121' : 'rgba(255,255,255,0.2)' }}>
                          <FiSend size={16} />
                        </button>
                      </div>
                      <p className="text-[9px] text-white/15 text-center mt-2">HingenIA puede cometer errores. Verifica la información importante.</p>
                    </div>
                  </div>
                  </div>
                  </div>
                </>
              )}

              {/* Fullscreen chat overlay */}
              {showChat && chatFullscreen && (
                <div className="fixed inset-0 z-[100] flex flex-col" style={{ background: '#212121' }}>
                  <div className="flex-1 flex flex-col max-w-[820px] w-full mx-auto overflow-hidden min-h-0">
                  {/* Reuse same header */}
                  <div className="px-4 py-2.5 flex items-center justify-between shrink-0">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center"><FiZap size={14} className="text-white" /></div>
                      <span className="text-[13px] font-medium text-white/90">HingenIA Chat</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setChatFullscreen(false)} className="p-1.5 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/10 transition-colors" title="Mitad pantalla">
                        <FiMinimize2 size={13} />
                      </button>
                      <button onClick={() => setShowChat(false)} className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors"><FiX size={15} /></button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto px-4">
                    <div className="max-w-[720px] mx-auto py-4 space-y-6">
                      {chatMessages.map((msg, i) => (
                        <div key={msg.id || i} className={msg.role === 'user' ? 'flex justify-end' : 'flex gap-3'}>
                          {msg.role === 'assistant' && <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center shrink-0 mt-0.5"><FiZap size={13} className="text-white/70" /></div>}
                          <div className={msg.role === 'user' ? 'inline-block max-w-[80%] bg-[#2f2f2f] text-white/90 rounded-2xl px-4 py-2.5 text-[14px]' : 'flex-1 min-w-0'}>
                            {msg.role === 'assistant' ? <div className="text-[14px] text-white/85 leading-relaxed chat-md" dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} /> : msg.content}
                          </div>
                        </div>
                      ))}
                      {chatLoading && <div className="flex gap-3"><div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center shrink-0"><FiZap size={13} className="text-white/70" /></div><div className="flex gap-1 py-2"><div className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce" /><div className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce" style={{animationDelay:'150ms'}} /><div className="w-1.5 h-1.5 bg-white/40 rounded-full animate-bounce" style={{animationDelay:'300ms'}} /></div></div>}
                      <div ref={chatEndRef} />
                    </div>
                  </div>
                  <div className="px-4 pb-4 pt-2 shrink-0">
                    <div className="max-w-[720px] mx-auto flex items-end gap-2 bg-[#2f2f2f] rounded-2xl border border-white/10 px-3 py-2">
                      <input value={chatInput} onChange={e => setChatInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChatMessage(chatInput); }}}
                        placeholder="Pregunta lo que quieras..." className="flex-1 bg-transparent text-[14px] text-white/90 placeholder-white/25 py-1.5 focus:outline-none" disabled={chatLoading} autoFocus />
                      <button onClick={() => sendChatMessage(chatInput)} disabled={chatLoading || !chatInput.trim()}
                        className="p-1.5 rounded-lg transition-all shrink-0" style={{ background: chatInput.trim() && !chatLoading ? 'white' : 'transparent', color: chatInput.trim() && !chatLoading ? '#212121' : 'rgba(255,255,255,0.2)' }}>
                        <FiSend size={16} />
                      </button>
                    </div>
                  </div>
                  </div>
                </div>
              )}
            </div>

            {/* ══ TEXT SELECTION POPUP (ChatGPT style) ══ */}
            {selectionPopup && !aiRefineLoading && (
              <div className="fixed z-[90]"
                onMouseDown={e => { if (e.target.tagName !== 'INPUT') e.preventDefault(); }}
                style={{ left: Math.min(selectionPopup.x - 155, window.innerWidth - 330), top: selectionPopup.y - 48 }}>
                {/* Main bar - ChatGPT style */}
                <div className="flex items-center bg-gray-800 rounded-full shadow-xl border border-gray-700/60 px-1 py-0.5 gap-0.5">
                  {/* Custom input */}
                  <div className="flex items-center flex-1 min-w-0">
                    <input
                      type="text"
                      placeholder="Editar o explicar..."
                      className="bg-transparent text-[12px] text-white/90 placeholder-white/35 px-3 py-1.5 focus:outline-none w-[160px]"
                      onMouseDown={e => e.stopPropagation()}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && e.target.value.trim()) refineSelectedText(e.target.value.trim());
                        if (e.key === 'Escape') { setSelectionPopup(null); savedRangeRef.current = null; }
                      }}
                    />
                    <button
                      onMouseDown={e => e.stopPropagation()}
                      onClick={(e) => {
                        const input = e.target.closest('div')?.parentElement?.querySelector('input');
                        if (input?.value.trim()) refineSelectedText(input.value.trim());
                      }}
                      className="p-1 text-white/40 hover:text-white/80 transition-colors shrink-0 mr-0.5">
                      <FiSend size={12} />
                    </button>
                  </div>
                  {/* Divider */}
                  <div className="w-px h-5 bg-white/15" />
                  {/* Quick action buttons */}
                  {[
                    { icon: <FiZap size={13} />, label: 'Mejorar', prompt: 'Mejora la redacción para que sea más clara y profesional.' },
                    { icon: <FiMaximize2 size={13} />, label: 'Expandir', prompt: 'Expande este texto con más detalle y ejemplos.' },
                    { icon: <FiCheck size={13} />, label: 'Corregir', prompt: 'Corrige errores ortográficos y gramaticales.' },
                  ].map(a => (
                    <button key={a.label} onClick={() => refineSelectedText(a.prompt)} title={a.label}
                      className="p-1.5 rounded-full hover:bg-white/15 transition-colors text-white/50 hover:text-white">
                      {a.icon}
                    </button>
                  ))}
                  {/* Close */}
                  <button onClick={() => { setSelectionPopup(null); savedRangeRef.current = null; }}
                    className="p-1 text-white/30 hover:text-white/60 transition-colors">
                    <FiX size={12} />
                  </button>
                </div>
              </div>
            )}
            {aiRefineLoading && (
              <div className="fixed z-[90] bg-gray-800 text-white rounded-full shadow-xl px-4 py-2 flex items-center gap-2 border border-gray-700/60"
                style={{ left: selectionPopup?.x - 60 || 200, top: (selectionPopup?.y || 200) - 44 }}>
                <div className="w-3 h-3 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
                <span className="text-[12px] text-white/70">Procesando...</span>
              </div>
            )}

            {/* ══ PERSONALIZATION MODAL ══ */}
            {showPersonalize && (
              <div className="fixed inset-0 z-[110] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.7)' }} onClick={() => setShowPersonalize(false)}>
                <div className="w-[480px] max-w-[92vw] max-h-[85vh] bg-[#1e1e1e] rounded-2xl overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                  {/* Header */}
                  <div className="px-5 py-4 border-b border-white/8 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-lg">🧠</div>
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-white/90">Cerebro del Asistente</h3>
                      <p className="text-[10px] text-white/35">Personaliza tu IA con instrucciones y conocimiento</p>
                    </div>
                    <button onClick={() => setShowPersonalize(false)} className="p-1.5 text-white/30 hover:text-white/70 rounded-lg hover:bg-white/5"><FiX size={16} /></button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-5 space-y-5">
                    {/* Identity */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-[11px] font-semibold text-white/50 uppercase">
                        <span>👤</span> Sobre ti
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-[10px] text-white/40 mb-1 block">Nombre</label>
                          <input value={personalization.name} onChange={e => setPersonalization(p => ({ ...p, name: e.target.value }))}
                            placeholder="Tu nombre"
                            className="w-full text-[13px] bg-white/5 border border-white/8 rounded-xl px-3 py-2 text-white/80 placeholder-white/20 focus:outline-none focus:border-indigo-500/50" />
                        </div>
                        <div>
                          <label className="text-[10px] text-white/40 mb-1 block">Rol / Ocupación</label>
                          <input value={personalization.role} onChange={e => setPersonalization(p => ({ ...p, role: e.target.value }))}
                            placeholder="Ej: CEO, Vendedor..."
                            className="w-full text-[13px] bg-white/5 border border-white/8 rounded-xl px-3 py-2 text-white/80 placeholder-white/20 focus:outline-none focus:border-indigo-500/50" />
                        </div>
                      </div>
                    </div>

                    {/* Instructions */}
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-[11px] font-semibold text-white/50 uppercase">
                        <span>⚡</span> Instrucciones del Asistente
                      </div>
                      <p className="text-[10px] text-white/30">Dale personalidad e instrucciones específicas a tu IA. Ejemplo: "Eres un experto en ventas B2B con 15 años de experiencia..."</p>
                      <textarea value={personalization.preferences} onChange={e => setPersonalization(p => ({ ...p, preferences: e.target.value }))}
                        placeholder={"Ej: Eres un experto en ventas telefónicas y marketing digital.\nResponde siempre con ejemplos prácticos.\nMi empresa se llama Hingenia y vendemos cursos online.\nQuiero que seas directo y uses datos cuando sea posible."}
                        rows={5}
                        className="w-full text-[13px] bg-white/5 border border-white/8 rounded-xl px-3 py-2.5 text-white/80 placeholder-white/20 focus:outline-none focus:border-indigo-500/50 resize-none leading-relaxed" />
                    </div>

                    {/* Knowledge base - Documents */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-[11px] font-semibold text-white/50 uppercase">
                          <span>📚</span> Base de Conocimiento
                        </div>
                        <button onClick={handleDocUpload} className="text-[10px] text-indigo-400 hover:text-indigo-300 flex items-center gap-1 font-medium">
                          <FiPlus size={11} /> Subir archivos
                        </button>
                      </div>
                      <p className="text-[10px] text-white/30">Sube documentos para que la IA los use como referencia. Soporta TXT, CSV, JSON, MD, HTML e imágenes.</p>

                      {chatDocs.length === 0 ? (
                        <button onClick={handleDocUpload}
                          className="w-full py-6 border-2 border-dashed border-white/10 rounded-xl text-white/25 hover:text-white/50 hover:border-white/20 transition-colors flex flex-col items-center gap-2">
                          <FiPlus size={20} />
                          <span className="text-[11px]">Arrastra o haz clic para subir archivos</span>
                          <span className="text-[9px] text-white/15">PDF, TXT, CSV, imágenes (max 10MB)</span>
                        </button>
                      ) : (
                        <div className="space-y-1.5">
                          {chatDocs.map((d, i) => (
                            <div key={i} className="flex items-center gap-2 bg-white/5 border border-white/8 rounded-lg px-3 py-2">
                              <span className="text-sm">{d.type === 'image' ? '🖼' : '📄'}</span>
                              <div className="flex-1 min-w-0">
                                <div className="text-[12px] text-white/70 truncate">{d.name}</div>
                                <div className="text-[9px] text-white/30">{(d.size / 1024).toFixed(0)} KB · {d.type === 'image' ? 'Imagen' : 'Texto'}</div>
                              </div>
                              <button onClick={() => setChatDocs(prev => prev.filter((_, j) => j !== i))} className="p-1 text-white/20 hover:text-red-400 rounded"><FiX size={12} /></button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="px-5 py-4 border-t border-white/8 flex items-center gap-3">
                    <div className="flex-1 text-[9px] text-white/20">
                      {chatDocs.length > 0 && `${chatDocs.length} documento${chatDocs.length > 1 ? 's' : ''} cargado${chatDocs.length > 1 ? 's' : ''}`}
                    </div>
                    <button onClick={() => setShowPersonalize(false)} className="px-4 py-2 text-[12px] text-white/50 hover:text-white/80 transition-colors">Cancelar</button>
                    <button onClick={() => { savePersonalization(personalization); setShowPersonalize(false); }}
                      className="px-5 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl text-[12px] font-medium hover:opacity-90 transition-opacity">
                      Guardar cambios
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ══ EBOOK PREVIEW ══ */}
            <EbookPreview
              isOpen={showEbook}
              onClose={() => setShowEbook(false)}
              title={noteTitle}
              content={noteContent}
              authorName={personalization.name || 'Autor'}
            />
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <FiEdit3 size={40} className="mx-auto mb-3 opacity-20" />
              <p className="text-sm font-medium text-gray-400">Selecciona o crea una nota</p>
              <p className="text-xs text-gray-300 mt-1">Notas con editor enriquecido, carpetas y Chat IA contextual</p>
              <button onClick={() => handleNewNote()} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors">
                <FiPlus size={12} className="inline mr-1" /> Nueva nota
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Note sidebar item ──
const NoteItem = ({ note, selected, onSelect, onDelete, stripHtml, indent }) => (
  <div onClick={() => onSelect(note)}
    className={`group px-3 py-2 border-b border-gray-100/50 cursor-pointer transition-colors ${indent ? 'pl-7' : ''}
      ${selected ? 'bg-blue-50 border-l-[3px] border-l-blue-500' : 'hover:bg-white border-l-[3px] border-l-transparent'}`}>
    <div className="flex items-start justify-between gap-1">
      <div className="flex-1 min-w-0">
        <p className="text-[11px] font-semibold text-gray-800 truncate">{note.title || 'Sin titulo'}</p>
        <p className="text-[9px] text-gray-400 mt-0.5 line-clamp-1">{stripHtml(note.content) || 'Nota vacia'}</p>
        <p className="text-[9px] text-gray-300 mt-0.5">
          {note.updatedAt?.toDate ? format(note.updatedAt.toDate(), 'd MMM, HH:mm', { locale: es }) : ''}
        </p>
      </div>
      <button onClick={(e) => { e.stopPropagation(); onDelete(note.id); }}
        className="p-0.5 rounded hover:bg-red-50 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
        <FiTrash2 size={10} />
      </button>
    </div>
  </div>
);

export default MyTasks;
